import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const LanguageContext = createContext(null);

const translations = {
  ar: {
    // Brand & App
    appName: 'مجموعة سيندرولي',
    appTagline: 'نظام إدارة المصانع والطباعة الصناعية',
    systemTitle: 'نظام الإدارة المتكامل ERP',

    // Navigation Sections & Items
    'nav.main': 'الرئيسية',
    'nav.dashboard': 'لوحة التحكم',
    'nav.notifications': 'الإشعارات والتنبيهات',
    'nav.sales': 'المبيعات والطلبيات',
    'nav.clients': 'إدارة العملاء',
    'nav.invoices': 'الفواتير والماليات',
    'nav.orders': 'أوامر الشغل والتصنيع',
    'nav.inventory': 'المخازن والمستودع',
    'nav.materials': 'المواد الخام والورق',
    'nav.products': 'دليل المنتجات',
    'nav.stockManagement': 'جرد وإدارة المخزون',
    'nav.materialWithdrawal': 'صرف خامات التشغيل',
    'nav.suppliers': 'دليل الموردين',
    'nav.purchases': 'فواتير المشتريات',
    'nav.reports': 'التقارير والمؤشرات',
    'nav.clientAnalytics': 'تحليلات العملاء',
    'nav.financialReport': 'التقرير المالي العام',
    'nav.financialStats': 'المؤشرات والرسوم المالية',
    'nav.clientReports': 'تقارير كشوف الحساب',
    'nav.settings': 'إعدادات النظام',
    'nav.userManagement': 'المستخدمين والصلاحيات',
    'nav.websiteSettings': 'إعدادات المنصة والموقع',
    'nav.coveraty': 'متجر كفراتي D2C',
    'nav.storeOrders': 'طلبات المتجر الإلكتروني',
    'nav.designsManager': 'إدارة التصاميم والنماذج',
    'nav.categories': 'أقسام وتصنيفات المتجر',
    'nav.attributes': 'ماركات وموديلات الهواتف',
    'nav.shipping': 'أسعار ومناطق الشحن',
    'nav.storeCustomers': 'سجل عملاء المتجر',

    // Top Header & Global Actions
    'header.searchPlaceholder': 'بحث سريع في العملاء، الأوامر، الفواتير... (Ctrl + K)',
    'header.help': 'المساعدة والدعم',
    'header.notifications': 'التنبيهات',
    'header.profile': 'الملف الشخصي',
    'header.logout': 'تسجيل الخروج',
    'header.collapseSidebar': 'طي القائمة الجانبية',
    'header.expandSidebar': 'فتح القائمة الجانبية',
    'header.noResults': 'لم يتم العثور على نتائج مطابقة',
    'header.searching': 'جاري البحث في قاعدة البيانات...',

    // Dashboard Cards & Stats
    'dashboard.revenue': 'الإيرادات',
    'dashboard.totalRevenue': 'إجمالي الإيرادات',
    'dashboard.totalOrders': 'إجمالي أوامر الشغل',
    'dashboard.activeOrders': 'أوامر قيد التشغيل',
    'dashboard.completedOrders': 'أوامر مكتملة ومسلمة',
    'dashboard.totalClients': 'إجمالي العملاء المسجلين',
    'dashboard.totalMaterials': 'أصناف المواد الخام',
    'dashboard.lowStockAlert': 'تنبيه نقص المخزون',
    'dashboard.recentOrders': 'أحدث أوامر الشغل المستلمة',
    'dashboard.orderStatusBreakdown': 'توزيع حالات التشغيل',
    'dashboard.revenueTimeseries': 'حركة الإيرادات والتحصيلات',
    'dashboard.today': 'اليوم',
    'dashboard.thisWeek': 'هذا الأسبوع',
    'dashboard.thisMonth': 'هذا الشهر',
    'dashboard.thisYear': 'هذا العام',
    'dashboard.vsLastMonth': 'مقارنة بالشهر السابق',

    // Statuses
    'status.pending': 'قيد الانتظار',
    'status.active': 'قيد التشغيل',
    'status.done': 'تم التنفيذ',
    'status.delivered': 'تم التسليم للعميل',
    'status.cancelled': 'ملغي',
    'status.paid': 'مسدد بالكامل',
    'status.partially_paid': 'مسدد جزئياً',
    'status.unpaid': 'غير مسدد',
    'status.draft': 'مسودة',
    'status.sent': 'تم الإرسال',

    // Order & Client Details Keys
    'orders.orderDetails': 'تفاصيل أمر الشغل',
    'orders.editOrder': 'تعديل أمر الشغل',
    'orders.clientInfo': 'بيانات العميل',
    'orders.orderInfo': 'بيانات أمر الشغل',
    'orders.financialInfo': 'البيانات المالية',
    'orders.additionalInfo': 'معلومات إضافية',
    'orders.backToOrders': 'العودة لأوامر الشغل',
    'orders.heightM': 'الارتفاع (م)',
    'orders.orderSize': 'مقاس الشغل',
    'orders.repeats': 'التكرار',
    'orders.deliveryDate': 'تاريخ التسليم',
    'orders.description': 'الوصف',
    'orders.price': 'السعر',
    'orders.deposit': 'العربون',
    'orders.remaining': 'المتبقي',
    'orders.createdBy': 'تم الإنشاء بواسطة',
    'orders.lastUpdated': 'آخر تحديث',
    'clients.clientDetails': 'بيانات العميل',
    'clients.editClient': 'تعديل بيانات العميل',
    'clients.backToClients': 'العودة للعملاء',
    'clients.clientOrders': 'طلبات العميل',
    'clients.noOrders': 'لا توجد طلبات لهذا العميل.',
    'clients.deleteConfirm': 'هل أنت متأكد من حذف هذا العميل؟',
    'auth.clientRegistration': 'تسجيل حساب عميل جديد',
    'auth.alreadyHaveAccount': 'لديك حساب بالفعل؟',
    'auth.registering': 'جاري التسجيل...',
    'auth.username': 'اسم المستخدم *',
    'auth.fullName': 'الاسم بالكامل *',
    'auth.email': 'البريد الإلكتروني *',
    'auth.phone': 'رقم الهاتف *',
    'auth.factoryNameOptional': 'اسم المصنع (اختياري)',
    'auth.addressOptional': 'العنوان (اختياري)',
    'auth.password': 'كلمة المرور *',
    'auth.confirmPassword': 'تأكيد كلمة المرور *',
    'auth.passwordsDoNotMatch': 'كلمتا المرور غير متطابقتين',
    'auth.passwordMinLength': 'يجب ألا تقل كلمة المرور عن 6 أحرف',
    'auth.registerFailed': 'فشل إنشاء الحساب',
    'auth.systemSubtitle': 'نظام إدارة المصانع والطباعة',
    'header.dashboardGuide': 'دليل لوحة التحكم',
    'header.contactSupport': 'الدعم الفني',
    'header.shortcuts': 'اختصارات لوحة المفاتيح',
    'common.imageNotAvailable': 'الصورة غير متوفرة',

    // Notification Titles & Types
    'notification.userDeleted': 'تم حذف مستخدم',
    'notification.supplierDeleted': 'تم حذف مورد',
    'notification.materialDeleted': 'تم حذف خامة',
    'notification.orderCreated': 'تم إنشاء أمر شغل',
    'notification.orderStatusUpdated': 'تم تحديث حالة أمر الشغل',
    'notification.invoiceCreated': 'تم إصدار فاتورة',
    'notification.clientCreated': 'تم تسجيل عميل جديد',
    'notification.purchaseCreated': 'تم إصدار أمر شراء',
    'notification.stockLow': 'تنبيه: انخفاض المخزون',
    'notification.noNotifications': 'لا توجد تنبيهات جديدة',
    'notification.noCategoryNotifications': 'لا توجد إشعارات لقسم',
    'notification.viewAll': 'عرض كافة التنبيهات',
    'notification.userCreated': 'تم إنشاء حساب المستخدم بنجاح',
    'notification.userUpdated': 'تم تحديث بيانات المستخدم',
    'notification.userActivated': 'تم تفعيل حساب المستخدم',
    'notification.userDeactivated': 'تم تعطيل حساب المستخدم',
    'notification.saveSuccess': 'تم حفظ التغييرات بنجاح',
    'notification.saveError': 'فشل حفظ التغييرات. يرجى المحاولة مرة أخرى.',

    // Client Analytics
    'clientAnalytics.title': 'تقرير تحليلات العملاء',
    'clientAnalytics.exportPDF': 'تصدير PDF',
    'clientAnalytics.refresh': 'تحديث',
    'clientAnalytics.totalClients': 'إجمالي العملاء',
    'clientAnalytics.totalRevenue': 'إجمالي الإيرادات',
    'clientAnalytics.totalPaid': 'المبالغ المحصلة',
    'clientAnalytics.collected': 'تم تحصيله',
    'clientAnalytics.totalOwed': 'المبالغ المستحقة (المتبقية)',
    'clientAnalytics.totalOrders': 'إجمالي الطلبات',
    'clientAnalytics.totalInvoices': 'إجمالي الفواتير',
    'clientAnalytics.avgOrdersPerClient': 'معدل الطلبات لكل عميل',
    'clientAnalytics.topPayingClients': 'أعلى 5 عملاء سداداً',
    'clientAnalytics.mostLoyalClient': 'العميل الأكثر ولاءً',
    'clientAnalytics.loyaltyScore': 'درجة الولاء',
    'clientAnalytics.paymentRate': 'نسبة السداد',
    'clientAnalytics.clientSince': 'عميل منذ',
    'clientAnalytics.days': 'يوم',
    'clientAnalytics.detailedStats': 'إحصائيات العملاء المفصلة',
    'clientAnalytics.searchPlaceholder': 'بحث في العملاء...',
    'clientAnalytics.clientName': 'اسم العميل',
    'clientAnalytics.contact': 'التواصل',
    'clientAnalytics.orders': 'الطلبات',
    'clientAnalytics.loyalty': 'الولاء',
    'clientAnalytics.status': 'الحالة',
    'clientAnalytics.actions': 'الإجراءات',
    'clientAnalytics.view': 'عرض',
    'clientAnalytics.report': 'تقرير',
    'clientAnalytics.noResults': 'لم يتم العثور على عملاء مطابقين للبحث',
    'clientAnalytics.loading': 'جاري تحميل تحليلات العملاء...',
    'clientAnalytics.error': 'فشل تحميل بيانات التحليلات',
    'clientAnalytics.retry': 'إعادة المحاولة',

    // Device Conflict
    'deviceConflict.blockedTitle': 'تم حظر تسجيل الدخول من جهاز آخر',
    'deviceConflict.blockedMessage': 'يُسمح بتسجيل الدخول من جهاز واحد فقط في نفس الوقت لأسباب أمنية.',
    'deviceConflict.terminatedTitle': 'تم إنهاء الجلسة',
    'deviceConflict.terminatedMessage': 'تم إنهاء جلستك الحالية لتسجيل الدخول من جهاز آخر.',
    'deviceConflict.securityTitle': 'تنبيه أمني',
    'deviceConflict.securityMessage': 'تم إنهاء الجلسة بسبب فحص أمني (تغير الجهاز أو الموقع).',
    'deviceConflict.invalidTitle': 'الجلسة غير صالحة',
    'deviceConflict.activeDevice': 'الجهاز النشط:',
    'deviceConflict.ipAddress': 'عنوان IP:',
    'deviceConflict.loginTime': 'وقت الدخول:',
    'deviceConflict.forceLogin': 'تسجيل الدخول هنا وإلغاء الآخر',
    'deviceConflict.loginAgain': 'تسجيل الدخول مجدداً',
    'deviceConflict.logout': 'تسجيل الخروج تماماً',
    'deviceConflict.securityNote': 'ملاحظة أمنية: هذا الإجراء يحمي حسابك من الوصول غير المصرح به.',
    'deviceConflict.anotherActive': 'يوجد جهاز آخر نشط حالياً',

    // Error & 404
    'error.unexpected': 'عذراً، حدث خطأ غير متوقع',
    'error.pageLoadError': 'حدث خطأ أثناء تحميل هذه الصفحة. يرجى إعادة تحميل الصفحة للمحاولة مرة أخرى.',
    'error.reload': 'إعادة التحميل',
    'notFound.title': '404',
    'notFound.message': 'الصفحة غير موجودة',
    'notFound.backHome': 'العودة للرئيسية',

    // Common Buttons & Actions
    'action.add': 'إضافة جديد',
    'action.save': 'حفظ التغييرات',
    'action.cancel': 'إلغاء',
    'action.edit': 'تعديل',
    'action.delete': 'حذف',
    'action.view': 'عرض التفاصيل',
    'action.search': 'بحث',
    'action.filter': 'تصفية',
    'action.reset': 'إعادة ضبط',
    'action.export': 'تصدير Excel/PDF',
    'action.print': 'طباعة إيصال/فاتورة',
    'action.refresh': 'تحديث البيانات',
    'action.confirm': 'تأكيد الإجراء',
    'action.close': 'إغلاق',
    'action.back': 'رجوع',
    'action.next': 'التالي',
    'action.upload': 'رفع ملف / صورة',
    'action.download': 'تحميل المستند',
    'action.toggleLanguage': 'English',

    // Table Headers & Fields
    'field.id': 'المعرف',
    'field.orderNumber': 'رقم الأمر',
    'field.invoiceNumber': 'رقم الفاتورة',
    'field.clientName': 'اسم العميل',
    'field.phone': 'رقم الهاتف',
    'field.date': 'التاريخ',
    'field.status': 'الحالة',
    'field.total': 'الإجمالي',
    'field.paid': 'المدفوع',
    'field.remaining': 'المتبقي',
    'field.actions': 'الإجراءات',
    'field.materialName': 'اسم المادة الخام',
    'field.quantity': 'الكمية',
    'field.unit': 'الوحدة',
    'field.price': 'السعر',
    'field.supplier': 'المورد',
    'field.notes': 'ملاحظات',
    'field.role': 'الدور والصلاحية',
    'field.currency': 'ج.م',

    // Roles
    'role.admin': 'مدير النظام التنفيذي',
    'role.receptionist': 'موظف استقبال ومبيعات',
    'role.designer': 'مصمم جرافيك ومسؤول فني',
    'role.worker': 'فني تشغيل وطباعة',
    'role.financial': 'محاسب ومراجع مالي',
    'role.client': 'عميل مسجل',

    // Auth & Login
    'login.title': 'نظام إدارة المصانع',
    'login.welcomeBack': 'مرحباً بعودتك',
    'login.signInSubtitle': 'تسجيل الدخول للوصول إلى لوحة التحكم',
    'login.usernameOrEmail': 'اسم المستخدم، البريد، أو رقم الهاتف',
    'login.phoneLabel': 'رقم الهاتف',
    'login.emailLabel': 'البريد الإلكتروني',
    'login.password': 'كلمة المرور',
    'login.signIn': 'تسجيل الدخول',
    'login.signingIn': 'جاري تسجيل الدخول...',
    'login.backToWebsite': 'العودة للموقع الرئيسي',
    'login.phoneLoginDesc': 'تسجيل الدخول برقم الهاتف بدون كلمة مرور للعملاء',
    'login.enterPhone': 'أدخل رقم هاتفك المسجل',
    'login.enterEmail': 'أدخل بريدك الإلكتروني',
    'login.enterUsernameOrEmail': 'أدخل اسم المستخدم، البريد، أو رقم الهاتف',
    'login.enterPassword': 'أدخل كلمة المرور',
    'login.optionalPhone': 'اختياري لتسجيل الدخول برقم الهاتف',
    'login.newClient': 'عميل جديد؟',
    'login.createAccount': 'إنشاء حساب جديد',
    'login.adminNotice': 'لدخول الإدارة وفريق العمل، يرجى استخدام بيانات حسابك المعتمدة.',
    'login.phoneLookupWarning': 'لم يتم العثور على حساب مرتبط بهذا الهاتف. إن كنت من موظفي الإدارة، يرجى تسجيل الدخول باسم المستخدم أو البريد وكلمة المرور.',

    // Clients Page
    'clients.title': 'إدارة العملاء',
    'clients.addNew': 'إضافة عميل جديد',
    'clients.edit': 'تعديل بيانات العميل',
    'clients.factoryName': 'اسم المصنع أو الشركة',
    'clients.address': 'العنوان',
    'clients.searchPlaceholder': 'بحث بالاسم، الهاتف، أو اسم المصنع...',

    // Orders Page
    'orders.title': 'أوامر الشغل والتصنيع',
    'orders.addOrder': 'إضافة أمر شغل جديد',
    'orders.sheetDimensions': 'المقاس (عرض × ارتفاع)',
    'orders.searchPlaceholder': 'بحث بالعميل، الهاتف، أو رقم الأمر...',
    'orders.allStates': 'كافة الحالات',

    // Invoices Page
    'invoices.title': 'الفواتير والتحصيلات',
    'invoices.newInvoice': 'إنشاء فاتورة جديدة',
    'invoices.invoiceNumber': 'رقم الفاتورة',
    'invoices.subtotal': 'المجموع الفرعي',
    'invoices.tax': 'الضريبة',
    'invoices.discount': 'الخصم',
    'invoices.totalAmount': 'المبلغ الإجمالي',

    // Inventory & Materials
    'inventory.title': 'جرد وإدارة المخزون',
    'inventory.startCount': 'بدء الجرد اليومي',
    'inventory.currentStock': 'المخزون الحالي',
    'inventory.minLevel': 'الحد الأدنى',
    'inventory.counted': 'تم الجرد',
    'inventory.pending': 'في انتظار الجرد',
    'materials.title': 'إدارة المواد الخام',
    'materials.subtitle': 'متابعة وتحديث أرصدة الخامات وتكاليف التشغيل',
    'materials.addMaterial': 'إضافة مادة خام',

    // Purchases & Suppliers
    'purchases.title': 'فواتير المشتريات والتوريدات',
    'purchases.newPurchase': 'أمر شراء جديد',
    'suppliers.title': 'دليل الموردين',
    'suppliers.addSupplier': 'إضافة مورد جديد',

    // Finance & Reports
    'finance.title': 'التقارير والمؤشرات المالية',
    'finance.income': 'إجمالي الإيرادات',
    'finance.expenses': 'إجمالي المصروفات',
    'finance.netProfit': 'صافي الربح',

    // Users
    'users.title': 'المستخدمين والصلاحيات',
    'users.addUser': 'إضافة مستخدم جديد',

    'coveraty.storeOrdersTitle': 'طلبات متجر كفراتي',
    'coveraty.storeOrdersSubtitle': 'إدارة ومتابعة طلبات المنتجات المستلمة عبر متجر الكفرات.',
    'coveraty.designsTitle': 'نماذج وتصاميم المتجر',
    'coveraty.categoriesTitle': 'أقسام وتصنيفات المتجر',
    'coveraty.attributesTitle': 'ماركات وموديلات الهواتف',
    'coveraty.attributesSubtitle': 'تهيئة وتحديد ماركات وموديلات الهواتف المعتمدة في المتجر.',
    'coveraty.addBrand': 'إضافة ماركة',
    'coveraty.editBrand': 'تعديل الماركة',
    'coveraty.addModel': 'إضافة موديل',
    'coveraty.editModel': 'تعديل الموديل',
    'coveraty.brands': 'الماركات',
    'coveraty.models': 'الموديلات',
    'coveraty.brandName': 'اسم الماركة',
    'coveraty.modelName': 'اسم الموديل',
    'coveraty.logoImage': 'شعار الماركة',
    'coveraty.uploadLogo': 'اضغط لرفع الشعار',
    'coveraty.brand': 'الماركة',
    'coveraty.selectBrand': 'اختر الماركة',
    'coveraty.designsSubtitle': 'رفع وإدارة تصاميم ونماذج كفرات الهواتف.',
    'coveraty.designTitle': 'عنوان التصميم',
    'coveraty.titlePlaceholder': 'مثال: موجات تجريدية',
    'coveraty.designImage': 'صورة التصميم',
    'coveraty.currentImage': 'الصورة الحالية (اضغط للاستبدال)',
    'coveraty.clickUpload': 'اضغط لرفع الصورة',
    'coveraty.imageHint': 'PNG أو JPG حتى 15 ميجابايت',
    'coveraty.allowedModels': 'الموديلات المتاحة',
    'coveraty.selectAll': 'تحديد الكل',
    'coveraty.deselectAll': 'إلغاء تحديد الكل',
    'coveraty.modelsSelected': 'موديلات محددة',
    'coveraty.createDesign': 'إنشاء التصميم',
    'coveraty.creatingDesign': 'جاري إنشاء التصميم...',
    'coveraty.updateDesign': 'تحديث التصميم',
    'coveraty.updatingDesign': 'جاري تحديث التصميم...',
    'coveraty.designsLibrary': 'مكتبة التصاميم',
    'coveraty.editDesign': 'تعديل التصميم',
    'coveraty.deleteDesign': 'حذف التصميم',
    'coveraty.shippingTitle': 'مناطق وأسعار الشحن',
    'coveraty.customersTitle': 'سجل عملاء المتجر',
    'coveraty.customersSubtitle': 'عرض قائمة عملاء المتجر الإلكتروني وإحصائيات طلباتهم.',
    'coveraty.totalStats': 'إجمالي الإحصائيات',
    'coveraty.lastOrder': 'آخر طلب',
    'coveraty.spent': 'المنفق',
    'coveraty.customerDetails': 'تفاصيل العميل',
    'coveraty.contactInfo': 'معلومات الاتصال',
    'coveraty.loyaltyStats': 'إحصائيات الولاء والطلبات',
    'coveraty.totalSpent': 'إجمالي المنفق',
    'coveraty.orderHistory': 'سجل الطلبات',
    'coveraty.fetchingOrders': 'جاري جلب الطلبات...',
    'coveraty.noOrderHistory': 'لا يوجد سجل طلبات سابق لهذا العميل.',
    'coveraty.product': 'المنتج',
    'coveraty.model': 'الموديل',
    'status.new': 'جديد',
    'status.printed': 'تمت الطباعة',
    'status.shipped': 'تم الشحن',
    'orders.each': 'للقطعة',

    // Confirmation & Feedback
    'msg.confirmDelete': 'هل أنت متأكد من رغبتك في حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.',
    'msg.success': 'تمت العملية بنجاح!',
    'msg.error': 'حدث خطأ أثناء معالجة الطلب. يرجى المحاولة مرة أخرى.',
    'msg.loading': 'جاري تحميل البيانات...',
    'msg.empty': 'لا توجد بيانات مسجلة حالياً.',

    // General & UI Page Keys
    exportPDF: 'تصدير كملف PDF',
    addCategory: 'إضافة قسم جديد',
    activeCategory: 'قسم نشط',
    noCategories: 'لا توجد أقسام مسجلة',
    createFirstCategory: 'أنشئ أول قسم لتنظيم المنتجات.',
    categoryName: 'اسم القسم',
    coverImage: 'صورة الغلاف',
    changeImage: 'تغيير الصورة',
    clickToUpload: 'اضغط لرفع الغلاف',
    cancel: 'إلغاء',
    create: 'إنشاء',
    save: 'حفظ',
    editCategory: 'تعديل القسم',
    shipping: 'إدارة مناطق الشحن',
    shippingSubtitle: 'إعداد وجهات الشحن وتكلفتها',
    addShippingZone: 'إضافة منطقة شحن',
    city: 'المدينة / المنطقة',
    shippingCost: 'تكلفة الشحن (ج.م)',
    actions: 'الإجراءات',
    noShippingZones: 'لا توجد مناطق شحن مسجلة بعد.',
    editShippingZone: 'تعديل منطقة الشحن',
    cityName: 'اسم المدينة',
    financialReport: 'التقرير المالي العام',
    financialReportSubtitle: 'نظرة شاملة ومفصلة على ماليات المؤسسة',
    notifications: 'التنبيهات والإشعارات',
    unread: 'غير مقروء',
    markAllAsRead: 'تحديد الكل كمقروء',
    deleteRead: 'حذف المقروء',
    noNotifications: 'لا توجد إشعارات',
    products: 'إدارة المنتجات',
    productsSubtitle: 'إدارة المنتجات وتركيبات الخامات والتصنيع',
    addProduct: 'إضافة منتج',
    searchProducts: 'بحث في المنتجات...',
    noProductsFound: 'لم يتم العثور على منتجات مطابقة',
    editProduct: 'تعديل المنتج',
    addMaterial: 'إضافة خامة',
    selectMaterial: 'اختر الخامة',
    home: 'الرئيسية',
    about: 'من نحن',
    services: 'خدماتنا',
    whyUs: 'لماذا سيندرولي',
    gallery: 'معرض الأعمال',
    contact: 'تواصل معنا',
    followUs: 'تابعنا',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    login: 'تسجيل الدخول',

    // Dashboard Extended
    'dashboard.orderStatus': 'حالات أوامر الشغل',
    'dashboard.ordersOverview': 'نظرة عامة على الطلبات',
    'dashboard.materialsByCategory': 'المواد حسب التصنيف',
    'dashboard.topClientsByRevenue': 'أعلى العملاء إيراداً',
    'dashboard.topClients': 'أفضل العملاء',
    'dashboard.clientAnalyticsOverview': 'نظرة عامة على تحليلات العملاء',
    'dashboard.mvpClient': 'العميل الأفضل',
    'dashboard.loyalClient': 'العميل الأكثر ولاءً',
    'dashboard.topBuyer': 'العميل الأكثر طلباً',
    'dashboard.loyaltyTier': 'مستوى الولاء',
    'dashboard.score': 'النقاط',
    'dashboard.viewFullReport': 'عرض التقرير الكامل',
    'dashboard.quickActions': 'إجراءات سريعة',
    'dashboard.viewClients': 'عرض العملاء',
    'dashboard.viewInvoices': 'عرض الفواتير',
    'dashboard.viewOrders': 'عرض أوامر الشغل',
    'dashboard.lowStock': 'مخزون منخفض',
    'dashboard.overall': 'شامل',
    'dashboard.inStock': 'في المخزن',
    'orders.generalOrder': 'أمر شغل عام',

    // Inventory & Stock
    'inventory.dailyTitle': 'الجرد اليومي للمخزون',
    'inventory.dailyDesc': 'متابعة وتسجيل حركات وأرصدة الجرد اليومي للمستودع',
    'inventory.dateLabel': 'تاريخ الجرد:',
    'inventory.startDailyCount': 'بدء الجرد اليومي',
    'inventory.summaryFor': 'ملخص جرد يوم',
    'inventory.totalMaterialsCount': 'إجمالي المواد:',
    'inventory.pendingCount': 'في انتظار الجرد:',
    'inventory.countedToday': 'تم جرده اليوم:',
    'inventory.currentStockLevels': 'مستويات المخزون الحالية',
    'inventory.minLevelLabel': 'الحد الأدنى:',
    'inventory.currentLabel': 'الحالي:',
    'inventory.todaysCounts': 'عمليات جرد اليوم',
    'inventory.systemStock': 'رصيد النظام',
    'inventory.actualCount': 'العد الفعلي',
    'inventory.difference': 'الفارق',
    'inventory.wastage': 'الهالك / العجز',
    'inventory.countedBy': 'تم الجرد بواسطة',
    'inventory.time': 'الوقت',
    'inventory.loss': 'عجز',
    'inventory.gain': 'فائض',
    'inventory.submitCount': 'تسجيل الجرد',
    'inventory.countModalTitle': 'تسجيل الجرد اليومي',
    'inventory.recordingFor': 'تسجيل ليوم:',
    'inventory.actualCountLabel': 'العد الفعلي (بواسطة الفني):',
    'inventory.countHint': 'سجل الكمية الفعلية الموجودة في المخزن',
    'inventory.notesLabel': 'ملاحظات:',
    'inventory.optionalNotes': 'ملاحظات اختيارية',
    'inventory.outOfStock': 'نفذت الكمية',
    'inventory.good': 'متوفر',

    // Material Withdrawal
    'withdrawal.title': 'صرف خامات التشغيل',
    'withdrawal.subtitle': 'تسجيل صرف المواد الخام من المستودع لأوامر الشغل (قطعة واحدة في كل مرة)',
    'withdrawal.withdrawCardTitle': 'صرف مادة خام',
    'withdrawal.selectMaterial': 'اختر المادة الخام *',
    'withdrawal.selectMaterialPlaceholder': 'اختر مادة خام من القائمة',
    'withdrawal.currentStock': 'المخزون الحالي:',
    'withdrawal.category': 'التصنيف:',
    'withdrawal.notes': 'ملاحظات الصرف (اختياري)',
    'withdrawal.notesPlaceholder': 'اكتب أي تفاصيل بخصوص عملية الصرف...',
    'withdrawal.piece': 'قطعة',
    'withdrawal.pieces': 'قطع',
    'withdrawal.noteInfo': 'ملاحظة: يتم تسجيل الصرف قطعة واحدة في كل مرة. للصرف المتعدد اضغط على زر الصرف بعدد القطع المطلوبة.',
    'withdrawal.button': 'صرف قطعة واحدة',
    'withdrawal.withdrawing': 'جاري الصرف...',
    'withdrawal.historyTitle': 'سجل حركات صرف اليوم',
    'withdrawal.date': 'التاريخ:',
    'withdrawal.noWithdrawals': 'لا توجد حركات صرف مسجلة لهذا اليوم',
    'withdrawal.successMsg': 'تم صرف قطعة واحدة بنجاح من',
    'withdrawal.selectError': 'يرجى اختيار مادة خام أولاً',
    'withdrawal.outOfStockError': 'المادة المختارة نفذت من المخزن',

    // Invoices
    'invoices.listTitle': 'سجل الفواتير والتحصيلات',
    'invoices.addInvoice': 'إنشاء فاتورة جديدة',
    'invoices.allStatus': 'كافة الحالات',
    'invoices.searchPlaceholder': 'بحث برقم الفاتورة أو اسم العميل...',
    'invoices.noInvoices': 'لا توجد فواتير مطابقة. قم بإنشاء أول فاتورة!',
    'invoices.invoiceHash': 'رقم الفاتورة',
    'invoices.client': 'العميل',
    'invoices.date': 'التاريخ',
    'invoices.created': 'تاريخ الإنشاء',
    'invoices.createdBy': 'تم الإنشاء بواسطة:',
    'invoices.ordersCount': 'الأوامر',
    'invoices.total': 'الإجمالي',
    'invoices.remaining': 'المتبقي',
    'invoices.actions': 'الإجراءات',
    'invoices.deleteConfirm': 'هل أنت متأكد من حذف هذه الفاتورة؟ سيتم إلغاء ربط كافة أوامر الشغل التابعة لها.',
    'invoices.print': 'طباعة الفاتورة',
    'invoices.backToInvoices': '← العودة لقائمة الفواتير',
    'invoices.editInvoice': 'تعديل الفاتورة',
    'invoices.billTo': 'فاتورة إلى:',
    'invoices.ordersSection': 'أوامر الشغل المدرجة',
    'invoices.addOrder': '+ إضافة أمر شغل',
    'invoices.sizeM': 'المقاس (متر)',
    'invoices.repeats': 'التكرار',
    'invoices.orderSize': 'حجم الأمر',
    'invoices.deposit': 'الدفعة المقدمة (العربون)',
    'invoices.summary': 'ملخص الحساب والماليات',
    'invoices.taxWithCurrency': 'الضريبة (ج.م)',
    'invoices.shipping': 'الشحن (ج.م)',
    'invoices.discountWithCurrency': 'الخصم (ج.م)',
    'invoices.discountApplied': 'يطبق على إجمالي قيمة الفاتورة',
    'invoices.updateInvoice': 'تحديث بيانات الفاتورة',
    'invoices.createInvoice': 'إنشاء الفاتورة',
    'invoices.saving': 'جاري الحفظ...',
    'invoices.scrollHint': '← اسحب لمشاهدة جميع الأعمدة →',
    'invoices.loading': 'جاري تحميل الفاتورة...',
    'invoices.notFound': 'الفاتورة غير موجودة',

    // Client Portal & Registration
    'portal.welcome': 'مرحباً،',
    'portal.logout': 'تسجيل الخروج',
    'portal.myOrders': 'أوامر الشغل الخاصة بي',
    'portal.noOrders': 'ليس لديك أي أوامر شغل حتى الآن.',
    'portal.orderId': 'رقم الأمر',
    'portal.type': 'النوع',
    'portal.allRightsReserved': 'جميع الحقوق محفوظة',
    'register.title': 'إنشاء حساب جديد',
    'register.subtitle': 'سجل بياناتك لمتابعة أوامرك وتصميماتك',
    'register.noPasswordNotice': 'لا تحتاج لكلمة مرور! سجّل دخولك مباشرة برقم هاتفك.',
    'register.fullName': 'الاسم بالكامل *',
    'register.fullNamePlaceholder': 'أدخل اسمك بالكامل',
    'register.phone': 'رقم الهاتف *',
    'register.phonePlaceholder': '010XXXXXXXX أو 011XXXXXXXX',
    'register.phoneHint': 'ستستخدم هذا الرقم لتسجيل الدخول لاحقاً',
    'register.email': 'البريد الإلكتروني (اختياري)',
    'register.factoryName': 'اسم المصنع أو الشركة (اختياري)',
    'register.address': 'العنوان (اختياري)',
    'register.submit': 'إنشاء الحساب',
    'register.submitting': 'جاري إنشاء الحساب...',
    'register.alreadyHaveAccount': 'لديك حساب بالفعل؟',
    'register.signInLink': 'تسجيل الدخول',
    'register.backToLogin': '← العودة لتسجيل الدخول',
    'orders.modalAddTitle': 'إضافة أمر شغل جديد',
    'orders.modalEditTitle': 'تعديل أمر الشغل',
    'orders.materialPrint': 'خامة (طباعة)',
    'orders.productItem': 'منتج (قطعة)',
    'orders.material': 'الخامة',
    'orders.product': 'المنتج',
    'orders.selectMaterial': 'اختر الخامة',
    'orders.selectProduct': 'اختر المنتج',
    'orders.quantity': 'الكمية',
    'orders.orderSizeAuto': 'حجم الأمر (متر)',
    'orders.orderSizeAutoHint': 'تلقائي = التكرار × الارتفاع',
    'orders.designLink': 'رابط التصميم',
    'orders.selectClient': 'اختر العميل',
    'orders.clientName': 'اسم العميل',
    'orders.updateDesignTitle': 'تحديث بيانات التصميم',
    'orders.viewDesign': 'عرض التصميم',
    'orders.noDesign': 'لا يوجد تصميم',
    'orders.noLink': 'لا يوجد رابط',
    'orders.noNotes': 'لا توجد ملاحظات',
    'orders.noOrders': 'لم يتم العثور على أوامر شغل',
    'orders.viewOnly': 'عرض فقط',
    'orders.saving': 'جاري الحفظ...',
    'orders.updateOrder': 'تحديث أمر الشغل',
    'orders.updateDesign': 'تحديث التصميم',
    'orders.type': 'النوع',
    'orders.loading': 'جاري تحميل أوامر الشغل...',
    'orders.noMaterials': 'لا توجد خامات متاحة.',
    'orders.noProducts': 'لا توجد منتجات متاحة.',

    // Materials Extended Keys
    'materials.materialName': 'اسم الخامة',
    'materials.category': 'القسم',
    'materials.unit': 'الوحدة',
    'materials.supplier': 'المورد',
    'materials.selectSupplier': 'اختر المورد',
    'materials.minStockLevel': 'الحد الأدنى للمخزون',
    'materials.currentStock': 'المخزون الحالي',
    'materials.costPerUnit': 'سعر التكلفة للوحدة (ج.م)',
    'materials.sellingPrice': 'سعر البيع (ج.م)',
    'materials.sellingPriceHint': 'سيتم استخدام هذا السعر مباشرة عند اختيار الخامة في الأوامر',
    'materials.description': 'الوصف',
    'materials.descriptionPlaceholder': 'أدخل وصف الخامة (اختياري)',
    'materials.saving': 'جاري الحفظ...',
    'materials.updateMaterial': 'تحديث بيانات الخامة',
    'materials.addNewMaterial': 'إضافة مادة خام جديدة',
    'materials.inStock': 'متوفر بالمخزن',
    'materials.lowStock': 'مخزون منخفض',
    'materials.outOfStock': 'نفذ المخزون',
    'materials.minLevel': 'الحد الأدنى',
    'materials.orderType': 'نوع الأمر',
    'materials.yes': 'نعم',
    'materials.no': 'لا',
    'materials.noSupplier': 'لا يوجد مورد',
    'materials.categories.paper': 'ورق',
    'materials.categories.ink': 'أحبار',
    'materials.categories.chemicals': 'كيماويات',
    'materials.categories.packaging': 'تغليف',
    'materials.categories.tools': 'أدوات',
    'materials.categories.other': 'أخرى',
    'materials.units.kg': 'كيلوجرام (كجم)',
    'materials.units.liter': 'لتر',
    'materials.units.piece': 'قطعة',
    'materials.units.box': 'صندوق',
    'materials.units.roll': 'رول',
    'materials.units.sheet': 'لوح / فرخ',

    // Suppliers Extended Keys
    'suppliers.subtitle': 'إدارة الموردين وسجلات الاتصال وحسابات التوريد',
    'suppliers.name': 'اسم المورد',
    'suppliers.contactPerson': 'المسؤول للتواصل',
    'suppliers.phone': 'رقم الهاتف',
    'suppliers.email': 'البريد الإلكتروني',
    'suppliers.address': 'العنوان',
    'suppliers.businessInfo': 'البيانات التجارية',
    'suppliers.paymentTerms': 'شروط الدفع',
    'suppliers.status': 'الحالة',
    'suppliers.materialsSupplied': 'الخامات الموردة',
    'suppliers.materialsSuppliedDesc': 'حدد الخامات التي يوفرها هذا المورد',
    'suppliers.selectedMaterials': 'الخامات المختارة',
    'suppliers.additionalNotes': 'ملاحظات إضافية',
    'suppliers.saving': 'جاري الحفظ...',
    'suppliers.updateSupplier': 'تحديث بيانات المورد',
    'suppliers.addNewSupplier': 'إضافة مورد جديد',
    'suppliers.searchPlaceholder': 'بحث بالاسم، المسؤول، أو الهاتف...',
    'suppliers.totalSuppliers': 'إجمالي الموردين',
    'suppliers.activeSuppliers': 'الموردين النشطين',
    'suppliers.active': 'نشط',
    'suppliers.inactive': 'غير نشط',
    'suppliers.noSuppliers': 'لم يتم العثور على موردين مسجلين',
    'suppliers.noSuppliersDesc': 'ابدأ بإضافة مورد جديد لإدارة سلسلة التوريد والمخازن',
    'suppliers.noMaterials': 'لم يتم تحديد خامات',
    'suppliers.deleteSelected': 'حذف المحددين',
    'suppliers.selectedCount': 'محدد',
    'suppliers.termsCash': 'نقداً',
    'suppliers.termsNet15': 'أجل 15 يوم',
    'suppliers.termsNet30': 'أجل 30 يوم',
    'suppliers.termsNet60': 'أجل 60 يوم',

    // Purchases Extended Keys
    'purchases.subtitle': 'إدارة وتتبع أوامر الشراء وجداول التوريدات',
    'purchases.purchaseNumber': 'رقم أمر الشراء',
    'purchases.orderInfo': 'بيانات أمر الشراء',
    'purchases.orderDate': 'تاريخ الطلب',
    'purchases.expectedDelivery': 'تاريخ الاستلام المتوقع',
    'purchases.items': 'أصناف التوريد',
    'purchases.addItem': '+ إضافة صنف',
    'purchases.selectMaterial': 'اختر الخامة',
    'purchases.quantity': 'الكمية',
    'purchases.unitCost': 'سعر الوحدة',
    'purchases.totalCost': 'التكلفة الإجمالية',
    'purchases.notesPlaceholder': 'ملاحظات إضافية أو تعليمات التوريد...',
    'purchases.noItemsYet': 'لم تتم إضافة أصناف بعد.',
    'purchases.selectSupplierFirst': 'اختر المورد أولاً لبدء إضافة الأصناف.',
    'purchases.statusOrdered': 'تم الطلب',
    'purchases.statusReceived': 'تم الاستلام',
    'purchases.markOrdered': 'تأكيد الطلب',
    'purchases.markReceived': 'تأكيد الاستلام',
    'purchases.saving': 'جاري الحفظ...',
    'purchases.updatePurchase': 'تحديث أمر الشراء',
    'purchases.createPurchase': 'إنشاء أمر الشراء',
    'purchases.tbd': 'قيد التحديد',
    'purchases.noPurchases': 'لم يتم العثور على أوامر شراء',

    // Search and Filters Reusable Keys
    'search.searchBtn': 'بحث',
    'search.allClients': 'كافة العملاء',
    'search.allStatuses': 'كافة الحالات',
    'search.fromDate': 'من تاريخ',
    'search.toDate': 'إلى تاريخ',
    'search.clearFilters': 'مسح التصفية',
    'search.defaultPlaceholder': 'بحث...',

    // Pagination Reusable Keys
    'pagination.showing': 'عرض',
    'pagination.to': 'إلى',
    'pagination.of': 'من أصل',
    'pagination.results': 'سجلات',
    'pagination.first': 'الصفحة الأولى',
    'pagination.prev': 'الصفحة السابقة',
    'pagination.next': 'الصفحة التالية',
    'pagination.last': 'الصفحة الأخيرة',

    // Users Extended Keys
    'users.role': 'الدور',
    'users.password': 'كلمة المرور',
    'users.passwordHint': '(اتركه فارغاً للإبقاء على الحالية)',
    'users.activate': 'تفعيل',
    'users.deactivate': 'تعطيل',
    'users.processing': 'جاري معالجة الإجراء، برجاء الانتظار...',
    'users.noUsers': 'لا يوجد مستخدمون مسجلون',

    // Financial Stats & Reports
    'finance.totalOrders': 'إجمالي الأوامر',
    'finance.totalRevenue': 'إجمالي الإيرادات',
    'finance.totalDeposits': 'إجمالي المقدمات',
    'finance.totalRemaining': 'إجمالي المتبقي',
    'finance.ordersByStatus': 'الأوامر حسب حالة التشغيل',
    'finance.count': 'العدد',
    'finance.totalValue': 'القيمة الإجمالية',
    'finance.exportPDF': 'تصدير كملف PDF',

    // Common Currency and Units
    'common.egp': 'ج.م',
    'common.meters': 'متر',
    'common.loading': 'جاري التحميل...',
    'common.processing': 'جاري المعالجة...',
    'status.all': 'الكل',
    'status.read': 'مقروء',

    // Purchases Additional
    'purchases.supplier': 'المورد',
    'purchases.selectSupplier': 'اختر المورد',
    'purchases.totalAmount': 'المبلغ الإجمالي',
    'purchases.loading': 'جاري تحميل أوامر الشراء...',
    'purchases.noPurchasesDesc': 'ابدأ بإنشاء أول أمر شراء للنظام.',
    'purchases.searchPlaceholder': 'بحث برقم أمر الشراء أو المورد...',

    // Products Additional
    'products.composition': 'التركيبة الخامية',
    'products.noMaterials': 'لا توجد خامات مضافة',
    'products.productName': 'اسم المنتج',
    'products.materialComposition': 'تركيبة المواد الخام',

    // Users Additional
    'users.allRoles': 'كافة الأدوار والصلاحيات',
    'users.username': 'اسم المستخدم',
    'users.fullName': 'الاسم بالكامل',
    'users.email': 'البريد الإلكتروني',
    'users.loading': 'جاري تحميل المستخدمين...',
    'users.editUser': 'تعديل المستخدم',

    // Notifications Additional
    'notifications.category': 'التصنيف',
    'notifications.allCategories': 'كافة التصنيفات',
    'notifications.payments': 'المدفوعات',
    'notifications.system': 'النظام',

    // Client Reports
    'clientReports.subtitle': 'عرض التفاصيل المالية وحسابات كل عميل بما في ذلك الأوامر، الفواتير، والمدفوعات',
    'clientReports.selectClient': 'اختر العميل:',
    'clientReports.selectPlaceholder': '-- اختر عميلاً لعرض تقريره --',
    'clientReports.noOrders': 'لا توجد أوامر شغل مسجلة لهذا العميل.',
    'clientReports.endpointNotFound': 'نقطة الاتصال غير موجودة. يرجى إعادة تشغيل السيرفر.',
    'clientReports.fetchError': 'فشل تحميل تقرير العميل',

    // Extended Finance
    'finance.totalPaid': 'إجمالي المدفوع',
    'finance.outstanding': 'المتبقي المستحق',
    'finance.activeClients': 'العملاء النشطون',
    'finance.totalInvoices': 'إجمالي الفواتير',
    'finance.totalOwed': 'إجمالي المديونية',
    'finance.orderAmount': 'قيمة الأوامر',
    'finance.invoiceAmount': 'قيمة الفواتير',
    'finance.paymentStats': 'إحصائيات السداد والتحصيل',
    'finance.avgOrderValue': 'متوسط قيمة الأمر',
    'finance.collectionRate': 'معدل التحصيل والتحصيل الفعلي',
    'finance.fullyPaidOrders': 'أوامر مسددة بالكامل',
    'finance.partiallyPaidOrders': 'أوامر مسددة جزئياً',
    'finance.unpaidOrders': 'أوامر غير مسددة',
    'finance.revenueByMonth': 'الإيرادات حسب الشهر',
    'finance.invoicesByStatus': 'الفواتير حسب حالة السداد',

    // Date Range and Reports
    'report.allTime': 'كل الأوقات',
    'report.last7Days': 'آخر 7 أيام',
    'report.lastMonth': 'الشهر الماضي',
    'report.lastQuarter': 'الربع الأخير',
    'report.lastYear': 'العام الماضي',
    'report.customRange': 'نطاق مخصص',

    // Store Order Statuses
    'store.orderNew': 'جديد',
    'store.orderPrinted': 'تمت الطباعة',
    'store.orderShipped': 'تم الشحن',
    'store.orderDelivered': 'تم التسليم',
    'store.orderCancelled': 'ملغي',

    // Relative Time
    'time.justNow': 'الآن',
    'time.mAgo': 'دقيقة مضت',
    'time.hAgo': 'ساعة مضت',
    'time.dAgo': 'يوم مضى',

    // Unauthorized
    'unauthorized.title': '403',
    'unauthorized.subtitle': 'غير مصرح بالدخول',
    'unauthorized.message': 'ليس لديك الصلاحيات الكافية للوصول إلى هذه الصفحة.',
    'unauthorized.goHome': 'العودة للرئيسية',

    // Shared & Page-specific Keys
    'orders.subtitle': 'أحدث أوامر التشغيل والإنتاج المستلمة',
    'action.select': 'تحديد',
    'action.update': 'تحديث',
    'action.create': 'إنشاء',
    'confirm.deleteClient': 'هل أنت متأكد من حذف هذا العميل؟',
    'categories': 'الأقسام والتصنيفات',
    'categoriesSubtitle': 'إدارة أقسام وتصنيفات منتجات وتصاميم المتجر',
    'field.category': 'القسم / التصنيف',
    'field.name': 'الاسم',
    'common.select': 'اختر...',
    'markAsRead': 'تحديد كمقروء',
    'users.searchPlaceholder': 'بحث باسم المستخدم أو الاسم أو البريد...',
    'register.alreadyRegistered': 'رقم الهاتف مسجل بالفعل. يرجى تسجيل الدخول بدلاً من ذلك.',

    // Website & Store Settings
    'websiteSettings.title': 'إعدادات الموقع والمتجر',
    'websiteSettings.subtitle': 'إدارة محتوى ومظهر الموقع الإلكتروني ومتجر كفراتي العام',
    'websiteSettings.mainWebsite': 'الموقع التعريفي الرئيسي',
    'websiteSettings.storeWebsite': 'متجر كفراتي (Coveraty Store)',
    'websiteSettings.hero': 'قسم الواجهة (Hero)',
    'websiteSettings.about': 'من نحن (About)',
    'websiteSettings.services': 'الخدمات (Services)',
    'websiteSettings.whyUs': 'لماذا تختارنا (Why Us)',
    'websiteSettings.gallery': 'معرض الأعمال (Gallery)',
    'websiteSettings.contact': 'بيانات التواصل (Contact)',
    'websiteSettings.branding': 'الهوية والألوان (Branding)',
    'websiteSettings.seo': 'تهيئة محركات البحث (SEO)',
    'websiteSettings.saveChanges': 'حفظ التعديلات',
    'websiteSettings.savedSuccess': 'تم حفظ الإعدادات بنجاح!',
    'websiteSettings.saveFailed': 'فشل حفظ الإعدادات',
    'websiteSettings.preview': 'معاينة الموقع',
    'landing.getStarted': 'ابدأ الآن',
    'landing.learnMore': 'اعرف المزيد',
    'landing.ourServices': 'خدماتنا ومنتجاتنا',
    'landing.whyChooseUs': 'لماذا تختار مجموعة سيندرولي',
    'landing.getInTouch': 'تواصل معنا مباشرة',
    'landing.followUs': 'تابعنا على منصات التواصل',
    'landing.scanToConnect': 'امسح الرمز للتواصل المباشر',
    'landing.allRightsReserved': 'جميع الحقوق محفوظة © مجموعة سيندرولي الصناعية',

    // Missing keys & localization additions
    'validation.phoneInvalid': 'يرجى إدخال رقم هاتف صحيح',
    'validation.emailInvalid': 'يرجى إدخال بريد إلكتروني صحيح',
    'msg.checkFields': 'يرجى التحقق من الحقول المطلوبة',
    'common.exportError': 'حدث خطأ أثناء تصدير الملف',
    'common.print': 'طباعة',
    'common.actions': 'الإجراءات',
    'common.view': 'عرض',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.previous': 'السابق',
    'common.next': 'التالي',
    'common.page': 'صفحة',
    'common.of': 'من',
    'common.pageOf': 'صفحة {current} من {total}',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'finance.paid': 'المدفوع',
    'client.loadError': 'فشل تحميل بيانات العميل',
    'client.notFound': 'العميل غير موجود',
    'client.name': 'اسم العميل',
    'client.phone': 'رقم الهاتف',
    'client.factoryName': 'اسم المصنع',
    'client.address': 'العنوان',
    'client.deleteError': 'فشل حذف العميل',
    'client.loadingClients': 'جاري تحميل العملاء...',
    'client.addNew': 'إضافة عميل جديد',
    'client.searchPlaceholder': 'البحث باسم العميل، الهاتف، أو المصنع...',
    'client.noClientsFound': 'لم يتم العثور على عملاء',
    'table.scrollPrompt': 'اسحب أفقياً للمزيد من الأعمدة',
    'orders.size': 'المقاس',
    'orders.status': 'الحالة',
    'orders.loadError': 'فشل تحميل أمر الشغل',
    'orders.notFound': 'أمر الشغل غير موجود',
    'orders.notes': 'ملاحظات',
    'orders.client': 'العميل',
    'field.createdAt': 'تاريخ الإنشاء',
    'field.email': 'البريد الإلكتروني',
    'field.address': 'العنوان',
    'field.value': 'القيمة',
    'field.orderAmount': 'قيمة الطلب',
    'field.invoiceAmount': 'قيمة الفاتورة',
    'field.month': 'الشهر',
    'sidebar.clients': 'العملاء',
    'nav.logout': 'تسجيل الخروج',
    'nav.clientPortal': 'بوابة العملاء',
    'notification.orderStatusChanged': 'تغيرت حالة أمر الشغل',
    'notification.orderUpdated': 'تحديث أمر الشغل',
    'notification.orderDeleted': 'حذف أمر الشغل',
    'notification.clientUpdated': 'تحديث بيانات العميل',
    'notification.clientDeleted': 'حذف العميل',
    'notification.invoiceUpdated': 'تحديث الفاتورة',
    'notification.materialCreated': 'إضافة مادة خام جديدة',
    'notification.materialUpdated': 'تحديث مادة خام',
    'notification.paymentReceived': 'تم استلام دفعة مالية',
    'notification.lowStockAlert': 'تنبيه نقص في المخزون',
    'notification.systemAlert': 'تنبيه نظام',
    'notification.allCaughtUp': 'لا توجد إشعارات جديدة حالياً',
    'notification.noNotificationsYet': 'لا توجد إشعارات حتى الآن',
    'notification.markAllAsRead': 'تحديد الكل كمقروء',
    'dashboard.kpi': 'مؤشر الأداء',
    'dashboard.orders': 'أوامر الشغل',
    'financialReport.revenueByMonth': 'الإيرادات حسب الشهر',
    'clients.clientName': 'اسم العميل',
    'coveraty.shipping': 'الشحن',
    'auth.singleDeviceLogin': 'تسجيل الدخول من جهاز واحد',
    'auth.loggingInFrom': 'جاري الدخول من جهاز:',
    'auth.phoneHint': 'تسجيل الدخول برقم الهاتف فقط — لا يلزم كلمة مرور لحسابات العملاء.',
    'auth.phoneNotFoundWarning': 'لم يتم العثور على حساب بهذا الرقم. إذا كنت موظفاً/مديراً، يرجى تسجيل الدخول باسم المستخدم أو البريد وكلمة المرور.',
    'auth.optionalForPhone': 'اختياري للدخول بالهاتف',
    'auth.securityNotice': 'ملاحظة أمنية: يُسمح بجلسة واحدة فقط في نفس الوقت. استخدم "تسجيل الدخول هنا" لإنهاء الجلسات الأخرى.',
    'auth.dontHaveAccount': 'ليس لديك حساب؟',
    'auth.register': 'إنشاء حساب جديد',
    'auth.login': 'تسجيل الدخول',
    'auth.loggingIn': 'جاري تسجيل الدخول...',
    'suppliers.confirmDeleteOne': 'هل أنت متأكد من حذف هذا المورد؟ لا يمكن التراجع عن هذا الإجراء.',
    'suppliers.confirmDeleteMultiple': 'هل أنت متأكد من حذف {count} موردين محددين؟',
    'suppliers.deleteFailed': 'فشل حذف المورد. يرجى المحاولة مرة أخرى.',
    'suppliers.loading': 'جاري تحميل الموردين...',
    'materials.confirmDelete': 'هل أنت متأكد من حذف هذه المادة الخام؟ لا يمكن التراجع عن هذا الإجراء.',
    'materials.deleteFailed': 'فشل حذف المادة الخام. يرجى المحاولة مرة أخرى.',
    'materials.allStockLevels': 'جميع مستويات المخزون',
    'materials.totalMaterials': 'إجمالي المواد الخام',
    'materials.lowStockItems': 'أصناف قاربت على النفاد',
    'unit.kg': 'كجم',
    'unit.sheet': 'فرخ / شيت',
    'unit.meter': 'متر',
    'unit.roll': 'رول',
    'unit.box': 'كرتونة',
    'unit.piece': 'قطعة',
    'unit.packet': 'باكت',
    'category.paper': 'ورق وكارتون',
    'category.ink': 'أحبار وطباعة',
    'category.plates': 'زنكات وزنكوجراف',
    'category.chemicals': 'كيماويات ومحاليل',
    'category.packaging': 'تغليف وتعبئة',
    'category.raw_material': 'مواد خام عامة',
    'category.general': 'عام',
    'coveraty.brandUpdated': 'تم تحديث الماركة بنجاح',
    'coveraty.brandCreated': 'تم إنشاء الماركة بنجاح',
    'coveraty.brandSaveError': 'خطأ أثناء حفظ الماركة',
    'coveraty.brandDeleteConfirm': 'هل أنت متأكد من حذف هذه الماركة؟',
    'coveraty.brandDeleted': 'تم حذف الماركة',
    'coveraty.brandDeleteError': 'خطأ أثناء حذف الماركة',
    'coveraty.modelUpdated': 'تم تحديث الموديل بنجاح',
    'coveraty.modelCreated': 'تم إنشاء الموديل بنجاح',
    'coveraty.modelSaveError': 'خطأ أثناء حفظ الموديل',
    'coveraty.modelDeleteConfirm': 'هل أنت متأكد من حذف هذا الموديل؟',
    'coveraty.modelDeleted': 'تم حذف الموديل',
    'coveraty.modelDeleteError': 'خطأ أثناء حذف الموديل',
    'coveraty.categoryFetchError': 'فشل تحميل التصنيفات',
    'coveraty.categoryUpdated': 'تم تحديث التصنيف بنجاح',
    'coveraty.categoryCreated': 'تم إنشاء التصنيف بنجاح',
    'coveraty.categorySaveError': 'خطأ أثناء حفظ التصنيف',
    'coveraty.categoryDeleteConfirm': 'هل أنت متأكد من حذف هذا التصنيف؟',
    'coveraty.categoryDeleted': 'تم حذف التصنيف بنجاح',
    'coveraty.categoryDeleteError': 'خطأ أثناء حذف التصنيف',
    'coveraty.designDeleteConfirm': 'هل أنت متأكد من حذف هذا التصميم؟',
    'coveraty.designDeleted': 'تم حذف التصميم بنجاح',
    'coveraty.dataLoadError': 'فشل تحميل البيانات',
    'coveraty.designFillRequired': 'يرجى ملء جميع الحقول واختيار موديل واحد على الأقل',
    'coveraty.designUpdated': 'تم تحديث التصميم بنجاح',
    'coveraty.designAdded': 'تمت إضافة التصميم بنجاح',
    'coveraty.shippingUpdated': 'تم تحديث منطقة الشحن بنجاح',
    'coveraty.shippingCreated': 'تم إنشاء منطقة الشحن بنجاح',
    'coveraty.shippingSaveError': 'خطأ أثناء حفظ منطقة الشحن',
    'coveraty.shippingDeleteConfirm': 'هل أنت متأكد من حذف منطقة الشحن هذه؟',
    'coveraty.shippingDeleted': 'تم حذف منطقة الشحن بنجاح',
    'coveraty.shippingDeleteError': 'خطأ أثناء حذف منطقة الشحن',
    'coveraty.ordersFetchError': 'فشل تحميل الطلبات',
    'coveraty.orderStatusUpdated': 'تم تحديث حالة الطلب',
    'coveraty.orderStatusUpdateError': 'فشل تحديث حالة الطلب',
    'coveraty.noImage': 'لا توجد صورة',
    'coveraty.customDesign': 'تصميم مخصص',
    'coveraty.unknownName': 'اسم غير معروف',
    'products.loadError': 'فشل تحميل المنتجات',
    'products.fillRequired': 'يرجى ملء الحقول المطلوبة',
    'products.updated': 'تم تحديث المنتج بنجاح',
    'products.created': 'تم إنشاء المنتج بنجاح',
    'products.saveError': 'فشل حفظ المنتج',
    'products.deleteConfirm': 'هل أنت متأكد من حذف هذا المنتج؟',
    'products.deleted': 'تم حذف المنتج بنجاح',
    'products.deleteError': 'فشل حذف المنتج',
    'orders.deleteConfirm': 'هل أنت متأكد من حذف هذا الطلب؟',
    'purchases.deleteFailed': 'فشل حذف فاتورة المشتريات. يرجى المحاولة مرة أخرى.',
    'websiteSettings.accessDenied': 'تم رفض الوصول',
    'websiteSettings.accessDeniedDesc': 'ليس لديك صلاحية للوصول إلى هذه الصفحة. يمكن للمديرين فقط تعديل إعدادات الموقع.',
    'websiteSettings.chooseImage': 'اختر صورة',
    'websiteSettings.chooseLogo': 'اختر الشعار',
    'websiteSettings.chooseQr': 'اختر رمز QR',
    'websiteSettings.remove': 'إزالة',
    'websiteSettings.uploading': 'جاري الرفع...',
    'websiteSettings.arField': 'بالعربية',
    'websiteSettings.enField': 'بالإنجليزية',
    'websiteSettings.titleField': 'العنوان',
    'websiteSettings.tagline': 'الشعار الترويجي (Tagline)',
    'websiteSettings.description': 'الوصف (Description)',
    'websiteSettings.mission': 'رسالتنا (Mission)',
    'websiteSettings.vision': 'رؤيتنا (Vision)',
    'websiteSettings.ctaText': 'نص زر الإجراء (CTA)',
    'websiteSettings.ctaLink': 'رابط زر الإجراء',
    'websiteSettings.bgImage': 'صورة الخلفية',
    'websiteSettings.chooseBgImage': 'اختر صورة الخلفية',
    'websiteSettings.sectionTitle': 'عنوان القسم',
    'websiteSettings.icon': 'الأيقونة',
    'websiteSettings.image': 'الصورة',
    'websiteSettings.category': 'التصنيف',
    'websiteSettings.phone': 'الهاتف',
    'websiteSettings.whatsapp': 'واتساب',
    'websiteSettings.email': 'البريد الإلكتروني',
    'websiteSettings.locationName': 'اسم الموقع الجغرافي',
    'websiteSettings.mapsEmbed': 'كود خرائط جوجل (Google Maps Embed)',
    'websiteSettings.facebook': 'رابط فيسبوك',
    'websiteSettings.instagram': 'رابط انستجرام',
    'websiteSettings.linkedin': 'رابط لينكد إن',
    'websiteSettings.qrCode': 'صورة رمز QR',
    'websiteSettings.logo': 'شعار الموقع',
    'websiteSettings.primaryColor': 'اللون الأساسي',
    'websiteSettings.secondaryColor': 'اللون الثانوي',
    'websiteSettings.accentColor': 'لون التمييز',
    'websiteSettings.gradientStart': 'بداية التدرج',
    'websiteSettings.gradientEnd': 'نهاية التدرج',
    'websiteSettings.metaTitle': 'عنوان الميتا (Meta Title)',
    'websiteSettings.metaDescription': 'وصف الميتا (Meta Description)',
    'websiteSettings.storeName': 'اسم المتجر',
    'websiteSettings.addService': 'إضافة خدمة',
    'websiteSettings.addFeature': 'إضافة ميزة',
    'websiteSettings.addGalleryItem': 'إضافة عنصر للمعرض',
    'websiteSettings.whyChooseStore': 'لماذا تختار المتجر',
    'websiteSettings.storeServices': 'خدمات المتجر',
    'websiteSettings.storeGallery': 'معرض المتجر',
    'websiteSettings.storeAbout': 'عن المتجر',
    'websiteSettings.storeHero': 'واجهة المتجر الرئيسية',
    'websiteSettings.serviceItem': 'الخدمة #{num}',
    'websiteSettings.featureItem': 'الميزة #{num}',
    'websiteSettings.galleryItem': 'عنصر المعرض #{num}',
    'websiteSettings.iconEmoji': 'الأيقونة (إيموجي)',
    'websiteSettings.active': 'نشط',
    'websiteSettings.keywords': 'الكلمات المفتاحية (مفصولة بفواصل)',
    'websiteSettings.storeLogo': 'شعار المتجر',
    'websiteSettings.loadFailed': 'فشل تحميل إعدادات الموقع',
    'websiteSettings.loading': 'جاري تحميل إعدادات الموقع...',
    'websiteSettings.mapLocation': 'اسم الموقع على الخريطة',
    'websiteSettings.mapEmbed': 'كود تضمين الخريطة (Google Maps Embed)',
    'common.image': 'الصورة',
    'orders.deleteFailed': 'فشل حذف أمر الشغل',
    'filter.allStatuses': 'كافة الحالات',
    'orders.updateStatus': 'تحديث الحالة',
    'orders.noDesignYet': 'لا يوجد تصميم حتى الآن',
    'header.shortcutsHelp': 'المساعدة واختصارات لوحة المفاتيح',
    'coveraty.designDeleteError': 'فشل حذف التصميم',
    'notification.supplierCreated': 'تمت إضافة مورد جديد',
    'notification.supplierUpdated': 'تم تحديث بيانات المورد',
    'notification.markReadError': 'فشل تحديد الإشعار كمقروء',
    'notification.allMarkedRead': 'تم تحديد جميع الإشعارات كمقروءة',
    'notification.markAllReadError': 'فشل تحديد الكل كمقروء',
    'notification.confirmDeleteOne': 'هل أنت متأكد من حذف هذا الإشعار؟',
    'notification.deleted': 'تم حذف الإشعار',
    'notification.deleteError': 'فشل حذف الإشعار',
    'notification.confirmDeleteAllRead': 'هل أنت متأكد من حذف كافة الإشعارات المقروءة؟',
    'notification.allReadDeleted': 'تم حذف جميع الإشعارات المقروءة',
    'notification.deleteAllError': 'فشل حذف الإشعارات',
    'invoices.officialInvoice': 'فاتورة ضريبية رسمية',
    'orders.jobTicket': 'أمر تشغيل وإنتاج'
  },

  en: {
    // Brand & App
    appName: 'Sendroli Group',
    appTagline: 'Industrial Printing & Factory Management ERP',
    systemTitle: 'Integrated Enterprise ERP',

    // Navigation Sections & Items
    'nav.main': 'MAIN',
    'nav.dashboard': 'Dashboard',
    'nav.notifications': 'Notifications',
    'nav.sales': 'SALES & ORDERS',
    'nav.clients': 'Client Management',
    'nav.invoices': 'Invoices & Billing',
    'nav.orders': 'Work Orders',
    'nav.inventory': 'INVENTORY & WAREHOUSE',
    'nav.materials': 'Raw Materials',
    'nav.products': 'Product Catalog',
    'nav.stockManagement': 'Stock Management',
    'nav.materialWithdrawal': 'Material Withdrawal',
    'nav.suppliers': 'Suppliers Directory',
    'nav.purchases': 'Purchase Invoices',
    'nav.reports': 'REPORTS & INSIGHTS',
    'nav.clientAnalytics': 'Client Analytics',
    'nav.financialReport': 'Financial Report',
    'nav.financialStats': 'Financial Charts',
    'nav.clientReports': 'Client Statements',
    'nav.settings': 'SYSTEM SETTINGS',
    'nav.userManagement': 'Users & Access Control',
    'nav.websiteSettings': 'Platform Configuration',
    'nav.coveraty': 'COVERATY STORE D2C',
    'nav.storeOrders': 'Online Store Orders',
    'nav.designsManager': 'Design Templates',
    'nav.categories': 'Store Categories',
    'nav.attributes': 'Brands & Phone Models',
    'nav.shipping': 'Shipping Zones & Rates',
    'nav.storeCustomers': 'E-Commerce Customers',

    // Top Header & Global Actions
    'header.searchPlaceholder': 'Quick search in clients, orders, invoices... (Ctrl + K)',
    'header.help': 'Help & Support',
    'header.notifications': 'Notifications',
    'header.profile': 'User Profile',
    'header.logout': 'Sign Out',
    'header.collapseSidebar': 'Collapse Navigation',
    'header.expandSidebar': 'Expand Navigation',
    'header.noResults': 'No matching records found',
    'header.searching': 'Searching enterprise database...',

    // Dashboard Cards & Stats
    'dashboard.revenue': 'Revenue',
    'dashboard.totalRevenue': 'Total Revenue',
    'dashboard.totalOrders': 'Total Work Orders',
    'dashboard.activeOrders': 'Orders in Production',
    'dashboard.completedOrders': 'Completed & Delivered',
    'dashboard.totalClients': 'Registered Clients',
    'dashboard.totalMaterials': 'Raw Material Types',
    'dashboard.lowStockAlert': 'Low Stock Alert',
    'dashboard.recentOrders': 'Recent Work Orders',
    'dashboard.orderStatusBreakdown': 'Order Status Breakdown',
    'dashboard.revenueTimeseries': 'Revenue & Collections Trend',
    'dashboard.today': 'Today',
    'dashboard.thisWeek': 'This Week',
    'dashboard.thisMonth': 'This Month',
    'dashboard.thisYear': 'This Year',
    'dashboard.vsLastMonth': 'vs. previous month',

    // Statuses
    'status.pending': 'Pending',
    'status.active': 'In Production',
    'status.done': 'Completed',
    'status.delivered': 'Delivered',
    'status.cancelled': 'Cancelled',
    'status.paid': 'Fully Paid',
    'status.partially_paid': 'Partially Paid',
    'status.unpaid': 'Unpaid',
    'status.draft': 'Draft',
    'status.sent': 'Sent',

    // Order & Client Details Keys
    'orders.orderDetails': 'Order Details',
    'orders.editOrder': 'Edit Order',
    'orders.clientInfo': 'Client Information',
    'orders.orderInfo': 'Order Information',
    'orders.financialInfo': 'Financial Information',
    'orders.additionalInfo': 'Additional Information',
    'orders.backToOrders': 'Back to Orders',
    'orders.heightM': 'Height (m)',
    'orders.orderSize': 'Order Size',
    'orders.repeats': 'Repeats',
    'orders.deliveryDate': 'Delivery Date',
    'orders.description': 'Description',
    'orders.price': 'Price',
    'orders.deposit': 'Deposit',
    'orders.remaining': 'Remaining',
    'orders.createdBy': 'Created By',
    'orders.lastUpdated': 'Last Updated',
    'clients.clientDetails': 'Client Details',
    'clients.editClient': 'Edit Client',
    'clients.backToClients': 'Back to Clients',
    'clients.clientOrders': 'Client Orders',
    'clients.noOrders': 'No orders found for this client.',
    'clients.deleteConfirm': 'Are you sure you want to delete this client?',
    'auth.clientRegistration': 'Client Registration',
    'auth.alreadyHaveAccount': 'Already have an account?',
    'auth.registering': 'Registering...',
    'auth.username': 'Username *',
    'auth.fullName': 'Full Name *',
    'auth.email': 'Email *',
    'auth.phone': 'Phone Number *',
    'auth.factoryNameOptional': 'Factory Name (Optional)',
    'auth.addressOptional': 'Address (Optional)',
    'auth.password': 'Password *',
    'auth.confirmPassword': 'Confirm Password *',
    'auth.passwordsDoNotMatch': 'Passwords do not match',
    'auth.passwordMinLength': 'Password must be at least 6 characters',
    'auth.registerFailed': 'Registration failed',
    'auth.systemSubtitle': 'Factory Management System',
    'header.dashboardGuide': 'Dashboard Guide',
    'header.contactSupport': 'Contact Support',
    'header.shortcuts': 'Keyboard Shortcuts',
    'common.imageNotAvailable': 'Image not available',

    // Notification Titles & Types
    'notification.userDeleted': 'User Deleted',
    'notification.supplierDeleted': 'Supplier Deleted',
    'notification.materialDeleted': 'Material Deleted',
    'notification.orderCreated': 'Work Order Created',
    'notification.orderStatusUpdated': 'Order Status Updated',
    'notification.invoiceCreated': 'Invoice Issued',
    'notification.clientCreated': 'Client Registered',
    'notification.purchaseCreated': 'Purchase Order Created',
    'notification.stockLow': 'Alert: Low Stock Threshold',
    'notification.noNotifications': 'No new notifications',
    'notification.noCategoryNotifications': 'No notifications for category',
    'notification.viewAll': 'View All Notifications',
    'notification.userCreated': 'User account created successfully',
    'notification.userUpdated': 'User profile updated',
    'notification.userActivated': 'User account activated',
    'notification.userDeactivated': 'User account deactivated',
    'notification.saveSuccess': 'Changes saved successfully',
    'notification.saveError': 'Failed to save changes. Please try again.',

    // Client Analytics
    'clientAnalytics.title': 'Client Analytics Report',
    'clientAnalytics.exportPDF': 'Export PDF',
    'clientAnalytics.refresh': 'Refresh',
    'clientAnalytics.totalClients': 'Total Clients',
    'clientAnalytics.totalRevenue': 'Total Revenue',
    'clientAnalytics.totalPaid': 'Total Collected',
    'clientAnalytics.collected': 'collected',
    'clientAnalytics.totalOwed': 'Total Outstanding',
    'clientAnalytics.totalOrders': 'Total Orders',
    'clientAnalytics.totalInvoices': 'Total Invoices',
    'clientAnalytics.avgOrdersPerClient': 'Avg Orders per Client',
    'clientAnalytics.topPayingClients': 'Top 5 Paying Clients',
    'clientAnalytics.mostLoyalClient': 'Most Loyal Client',
    'clientAnalytics.loyaltyScore': 'Loyalty Score',
    'clientAnalytics.paymentRate': 'Payment Rate',
    'clientAnalytics.clientSince': 'Client Since',
    'clientAnalytics.days': 'days',
    'clientAnalytics.detailedStats': 'Detailed Client Statistics',
    'clientAnalytics.searchPlaceholder': 'Search clients...',
    'clientAnalytics.clientName': 'Client Name',
    'clientAnalytics.contact': 'Contact',
    'clientAnalytics.orders': 'Orders',
    'clientAnalytics.loyalty': 'Loyalty',
    'clientAnalytics.status': 'Status',
    'clientAnalytics.actions': 'Actions',
    'clientAnalytics.view': 'View',
    'clientAnalytics.report': 'Report',
    'clientAnalytics.noResults': 'No clients found matching your search',
    'clientAnalytics.loading': 'Loading client analytics...',
    'clientAnalytics.error': 'Failed to load client statistics',
    'clientAnalytics.retry': 'Try Again',

    // Device Conflict
    'deviceConflict.blockedTitle': 'Device Login Blocked',
    'deviceConflict.blockedMessage': 'Only one device can be logged in at a time for your security.',
    'deviceConflict.terminatedTitle': 'Session Terminated',
    'deviceConflict.terminatedMessage': 'Your session was ended because another device logged in.',
    'deviceConflict.securityTitle': 'Security Alert',
    'deviceConflict.securityMessage': 'Your session was terminated due to a security check (device or location change).',
    'deviceConflict.invalidTitle': 'Session Invalid',
    'deviceConflict.activeDevice': 'Active Device:',
    'deviceConflict.ipAddress': 'IP Address:',
    'deviceConflict.loginTime': 'Login Time:',
    'deviceConflict.forceLogin': 'Force Login Here',
    'deviceConflict.loginAgain': 'Login Again',
    'deviceConflict.logout': 'Logout Completely',
    'deviceConflict.securityNote': 'Security Note: This feature protects your account from unauthorized access.',
    'deviceConflict.anotherActive': 'Another Device Active',

    // Error & 404
    'error.unexpected': 'Oops! An unexpected error occurred',
    'error.pageLoadError': 'An error occurred while loading this page. Please reload the page to try again.',
    'error.reload': 'Reload',
    'notFound.title': '404',
    'notFound.message': 'Page not found',
    'notFound.backHome': 'Back to Dashboard',

    // Common Buttons & Actions
    'action.add': 'Create New',
    'action.save': 'Save Changes',
    'action.cancel': 'Cancel',
    'action.edit': 'Edit',
    'action.delete': 'Delete',
    'action.view': 'View Details',
    'action.search': 'Search',
    'action.filter': 'Filter',
    'action.reset': 'Reset',
    'action.export': 'Export Report',
    'action.print': 'Print Document',
    'action.refresh': 'Refresh Data',
    'action.confirm': 'Confirm Action',
    'action.close': 'Close',
    'action.back': 'Back',
    'action.next': 'Next',
    'action.upload': 'Upload Asset',
    'action.download': 'Download File',
    'action.toggleLanguage': 'العربية',

    // Table Headers & Fields
    'field.id': 'ID',
    'field.orderNumber': 'Order #',
    'field.invoiceNumber': 'Invoice #',
    'field.clientName': 'Client Name',
    'field.phone': 'Phone Number',
    'field.date': 'Date',
    'field.status': 'Status',
    'field.total': 'Total',
    'field.paid': 'Paid',
    'field.remaining': 'Remaining',
    'field.actions': 'Actions',
    'field.materialName': 'Material Name',
    'field.quantity': 'Quantity',
    'field.unit': 'Unit',
    'field.price': 'Price',
    'field.supplier': 'Supplier',
    'field.notes': 'Notes',
    'field.role': 'Assigned Role',
    'field.currency': 'EGP',

    // Roles
    'role.admin': 'Enterprise Administrator',
    'role.receptionist': 'Front Desk Receptionist',
    'role.designer': 'Design & Artwork Specialist',
    'role.worker': 'Production & Print Specialist',
    'role.financial': 'Financial Controller',
    'role.client': 'Registered Client',

    // Auth & Login
    'login.title': 'Factory Management System',
    'login.welcomeBack': 'Welcome Back',
    'login.signInSubtitle': 'Sign in to access your dashboard',
    'login.usernameOrEmail': 'Username, Email or Phone',
    'login.phoneLabel': 'Phone Number',
    'login.emailLabel': 'Email Address',
    'login.password': 'Password',
    'login.signIn': 'Sign In',
    'login.signingIn': 'Signing In...',
    'login.backToWebsite': 'Back to Website',
    'login.phoneLoginDesc': 'Client direct login with verified phone number',
    'login.enterPhone': 'Enter your registered phone number',
    'login.enterEmail': 'Enter your email address',
    'login.enterUsernameOrEmail': 'Enter username, email, or phone',
    'login.enterPassword': 'Enter your password',
    'login.optionalPhone': 'Optional for phone login',
    'login.newClient': 'New Client?',
    'login.createAccount': 'Create an account',
    'login.adminNotice': 'For admin/team access, please use your existing credentials.',
    'login.phoneLookupWarning': 'No account found for this phone number. If you are a staff member, try logging in with username or email and your password.',

    // Clients Page
    'clients.title': 'Client Management',
    'clients.addNew': 'Add New Client',
    'clients.edit': 'Edit Client',
    'clients.factoryName': 'Factory / Company Name',
    'clients.address': 'Address',
    'clients.searchPlaceholder': 'Search by name, phone, or factory...',

    // Orders Page
    'orders.title': 'Work Orders Management',
    'orders.addOrder': 'Add New Order',
    'orders.sheetDimensions': 'Dimensions (W x H)',
    'orders.searchPlaceholder': 'Search by client name, phone, or order #...',
    'orders.allStates': 'All States',

    // Invoices Page
    'invoices.title': 'Invoices & Billing',
    'invoices.newInvoice': 'New Invoice',
    'invoices.invoiceNumber': 'Invoice #',
    'invoices.subtotal': 'Subtotal',
    'invoices.tax': 'Tax',
    'invoices.discount': 'Discount',
    'invoices.totalAmount': 'Grand Total',

    // Inventory & Materials
    'inventory.title': 'Inventory & Stock Management',
    'inventory.startCount': 'Start Daily Count',
    'inventory.currentStock': 'Current Stock',
    'inventory.minLevel': 'Min Stock Level',
    'inventory.counted': 'Counted',
    'inventory.pending': 'Pending Count',
    'materials.title': 'Raw Materials Management',
    'materials.subtitle': 'Manage your inventory materials and track stock levels',
    'materials.addMaterial': 'Add Material',

    // Purchases & Suppliers
    'purchases.title': 'Purchase Orders',
    'purchases.newPurchase': 'New Purchase Order',
    'suppliers.title': 'Suppliers Management',
    'suppliers.addSupplier': 'Add Supplier',

    // Finance & Reports
    'finance.title': 'Financial Reports & Accounting',
    'finance.income': 'Total Income',
    'finance.expenses': 'Total Expenses',
    'finance.netProfit': 'Net Profit',

    // Users
    'users.title': 'Users & Access Control',
    'users.addUser': 'Add User',

    // Coveraty Admin
    'coveraty.storeOrdersTitle': 'Store Orders',
    'coveraty.storeOrdersSubtitle': 'Manage and track product orders placed through the custom cover store.',
    'coveraty.designsTitle': 'Design Templates',
    'coveraty.categoriesTitle': 'Store Categories',
    'coveraty.attributesTitle': 'Brands & Phone Models',
    'coveraty.attributesSubtitle': 'Configure mobile brands and models allowed in the store.',
    'coveraty.addBrand': 'Add Brand',
    'coveraty.editBrand': 'Edit Brand',
    'coveraty.addModel': 'Add Model',
    'coveraty.editModel': 'Edit Model',
    'coveraty.brands': 'Brands',
    'coveraty.models': 'Models',
    'coveraty.brandName': 'Brand Name',
    'coveraty.modelName': 'Model Name',
    'coveraty.logoImage': 'Logo Image',
    'coveraty.uploadLogo': 'Click to upload logo',
    'coveraty.brand': 'Brand',
    'coveraty.selectBrand': 'Select Brand',
    'coveraty.designsSubtitle': 'Upload and manage your custom phone case designs.',
    'coveraty.designTitle': 'Design Title',
    'coveraty.titlePlaceholder': 'e.g. Abstract Waves',
    'coveraty.designImage': 'Design Image',
    'coveraty.currentImage': 'Current image (click to replace)',
    'coveraty.clickUpload': 'Click to upload image',
    'coveraty.imageHint': 'PNG, JPG up to 15MB',
    'coveraty.allowedModels': 'Allowed Models',
    'coveraty.selectAll': 'Select All',
    'coveraty.deselectAll': 'Deselect All',
    'coveraty.modelsSelected': 'models selected',
    'coveraty.createDesign': 'Create Design',
    'coveraty.creatingDesign': 'Creating Design...',
    'coveraty.updateDesign': 'Update Design',
    'coveraty.updatingDesign': 'Updating Design...',
    'coveraty.designsLibrary': 'Design Library',
    'coveraty.editDesign': 'Edit Design',
    'coveraty.deleteDesign': 'Delete Design',
    'coveraty.shippingTitle': 'Shipping Zones & Rates',
    'coveraty.customersTitle': 'Store Customers',
    'coveraty.customersSubtitle': 'View active customers ordering custom products and their loyalty stats.',
    'coveraty.totalStats': 'Total Stats',
    'coveraty.lastOrder': 'Last Order',
    'coveraty.spent': 'Spent',
    'coveraty.customerDetails': 'Customer Details',
    'coveraty.contactInfo': 'Contact Info',
    'coveraty.loyaltyStats': 'Loyalty Stats',
    'coveraty.totalSpent': 'Total Spent',
    'coveraty.orderHistory': 'Order History',
    'coveraty.fetchingOrders': 'Fetching orders...',
    'coveraty.noOrderHistory': 'No order history found for this customer.',
    'coveraty.product': 'Product',
    'coveraty.model': 'Model',
    'status.new': 'New',
    'status.printed': 'Printed',
    'status.shipped': 'Shipped',
    'orders.each': 'each',

    // Confirmation & Feedback
    'msg.confirmDelete': 'Are you sure you want to delete this record? This action cannot be undone.',
    'msg.success': 'Operation completed successfully!',
    'msg.error': 'An unexpected error occurred. Please try again.',
    'msg.loading': 'Loading data...',
    'msg.empty': 'No records found.',

    // General & UI Page Keys
    exportPDF: 'Export to PDF',
    addCategory: 'Add Category',
    activeCategory: 'Active Category',
    noCategories: 'No Categories Yet',
    createFirstCategory: 'Create your first category to start organizing your products.',
    categoryName: 'Category Name',
    coverImage: 'Cover Image',
    changeImage: 'Change Image',
    clickToUpload: 'Click to upload cover',
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    editCategory: 'Edit Category',
    shipping: 'Shipping Manager',
    shippingSubtitle: 'Configure shipping locations and their corresponding fees.',
    addShippingZone: 'Add Zone',
    city: 'City / Zone',
    shippingCost: 'Shipping Cost (EGP)',
    actions: 'Actions',
    noShippingZones: 'No shipping zones defined yet.',
    editShippingZone: 'Edit Shipping Zone',
    cityName: 'City Name',
    financialReport: 'Financial Report',
    financialReportSubtitle: 'Comprehensive overview of your business finances',
    notifications: 'Notifications',
    unread: 'unread',
    markAllAsRead: 'Mark all as read',
    deleteRead: 'Delete read',
    noNotifications: 'No notifications',
    products: 'Products Management',
    productsSubtitle: 'Manage your products and their material compositions',
    addProduct: 'Add Product',
    searchProducts: 'Search products...',
    noProductsFound: 'No products found',
    editProduct: 'Edit Product',
    addMaterial: 'Add Material',
    selectMaterial: 'Select Material',
    home: 'Home',
    about: 'About',
    services: 'Services',
    whyUs: 'Why Us',
    gallery: 'Gallery',
    contact: 'Contact',
    followUs: 'Follow Us',
    phone: 'Phone',
    email: 'Email',
    login: 'Login',

    // Dashboard Extended
    'dashboard.orderStatus': 'Order Status',
    'dashboard.ordersOverview': 'Orders Overview',
    'dashboard.materialsByCategory': 'Materials by Category',
    'dashboard.topClientsByRevenue': 'Top Clients by Revenue',
    'dashboard.topClients': 'Top Clients',
    'dashboard.clientAnalyticsOverview': 'Client Analytics Overview',
    'dashboard.mvpClient': 'MVP Client',
    'dashboard.loyalClient': 'Loyal Client',
    'dashboard.topBuyer': 'Top Buyer',
    'dashboard.loyaltyTier': 'Loyalty Tier',
    'dashboard.score': 'Score',
    'dashboard.viewFullReport': 'View Full Report',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.viewClients': 'View Clients',
    'dashboard.viewInvoices': 'View Invoices',
    'dashboard.viewOrders': 'View Orders',
    'dashboard.lowStock': 'Low Stock',
    'dashboard.overall': 'Overall',
    'dashboard.inStock': 'In Stock',
    'orders.generalOrder': 'General Order',

    // Inventory & Stock
    'inventory.dailyTitle': 'Daily Inventory',
    'inventory.dailyDesc': 'Track daily stock counts and manage inventory',
    'inventory.dateLabel': 'Inventory Date:',
    'inventory.startDailyCount': 'Start Daily Count',
    'inventory.summaryFor': 'Inventory Summary for',
    'inventory.totalMaterialsCount': 'Total Materials:',
    'inventory.pendingCount': 'Pending Count:',
    'inventory.countedToday': 'Counted Today:',
    'inventory.currentStockLevels': 'Current Stock Levels',
    'inventory.minLevelLabel': 'Min Level:',
    'inventory.currentLabel': 'Current:',
    'inventory.todaysCounts': "Today's Inventory Counts",
    'inventory.systemStock': 'System Stock',
    'inventory.actualCount': 'Actual Count',
    'inventory.difference': 'Difference',
    'inventory.wastage': 'Wastage',
    'inventory.countedBy': 'Counted By',
    'inventory.time': 'Time',
    'inventory.loss': 'loss',
    'inventory.gain': 'gain',
    'inventory.submitCount': 'Submit Count',
    'inventory.countModalTitle': 'Daily Inventory Count',
    'inventory.recordingFor': 'Recording for:',
    'inventory.actualCountLabel': 'Actual Count (by Worker):',
    'inventory.countHint': 'Count what you actually see in inventory',
    'inventory.notesLabel': 'Notes:',
    'inventory.optionalNotes': 'Optional notes',
    'inventory.outOfStock': 'Out of Stock',
    'inventory.good': 'Good',

    // Material Withdrawal
    'withdrawal.title': 'Material Withdrawal',
    'withdrawal.subtitle': 'Record material withdrawals from inventory (one piece at a time)',
    'withdrawal.withdrawCardTitle': 'Withdraw Material',
    'withdrawal.selectMaterial': 'Select Material *',
    'withdrawal.selectMaterialPlaceholder': 'Select a material',
    'withdrawal.currentStock': 'Current Stock:',
    'withdrawal.category': 'Category:',
    'withdrawal.notes': 'Notes (Optional)',
    'withdrawal.notesPlaceholder': 'Add any notes about this withdrawal...',
    'withdrawal.piece': 'piece',
    'withdrawal.pieces': 'pieces',
    'withdrawal.noteInfo': 'Note: Each withdrawal is for 1 piece. To withdraw multiple pieces, click the withdraw button multiple times.',
    'withdrawal.button': 'Withdraw 1 Piece',
    'withdrawal.withdrawing': 'Withdrawing...',
    'withdrawal.historyTitle': "Today's Withdrawals",
    'withdrawal.date': 'Date:',
    'withdrawal.noWithdrawals': 'No withdrawals recorded for this date',
    'withdrawal.successMsg': 'Successfully withdrew 1 piece of',
    'withdrawal.selectError': 'Please select a material',
    'withdrawal.outOfStockError': 'Selected material is out of stock',

    // Invoices
    'invoices.listTitle': 'Invoices',
    'invoices.addInvoice': 'Add Invoice',
    'invoices.allStatus': 'All Status',
    'invoices.searchPlaceholder': 'Search by invoice number or client name...',
    'invoices.noInvoices': 'No invoices found. Create your first invoice!',
    'invoices.invoiceHash': 'Invoice #',
    'invoices.client': 'Client',
    'invoices.date': 'Date',
    'invoices.created': 'Created',
    'invoices.createdBy': 'Created By:',
    'invoices.ordersCount': 'Orders',
    'invoices.total': 'Total',
    'invoices.remaining': 'Remaining',
    'invoices.actions': 'Actions',
    'invoices.deleteConfirm': 'Are you sure you want to delete this invoice? All orders within will be removed.',
    'invoices.print': 'Print',
    'invoices.backToInvoices': '← Back to Invoices',
    'invoices.editInvoice': 'Edit Invoice',
    'invoices.billTo': 'Bill To:',
    'invoices.ordersSection': 'Orders',
    'invoices.addOrder': '+ Add Order',
    'invoices.sizeM': 'Size (m)',
    'invoices.repeats': 'Repeats',
    'invoices.orderSize': 'Order Size',
    'invoices.deposit': 'Deposit',
    'invoices.summary': 'Invoice Summary',
    'invoices.taxWithCurrency': 'Tax (EGP)',
    'invoices.shipping': 'Shipping (EGP)',
    'invoices.discountWithCurrency': 'Discount (EGP)',
    'invoices.discountApplied': 'Applied to the whole invoice total',
    'invoices.updateInvoice': 'Update Invoice',
    'invoices.createInvoice': 'Create Invoice',
    'invoices.saving': 'Saving...',
    'invoices.scrollHint': '← Scroll to see all columns →',
    'invoices.loading': 'Loading invoice...',
    'invoices.notFound': 'Invoice not found',

    // Client Portal & Registration
    'portal.welcome': 'Welcome,',
    'portal.logout': 'Logout',
    'portal.myOrders': 'My Orders',
    'portal.noOrders': "You don't have any orders yet.",
    'portal.orderId': 'Order ID',
    'portal.type': 'Type',
    'portal.allRightsReserved': 'All rights reserved.',
    'register.title': 'Create Account',
    'register.subtitle': 'Register to manage your orders',
    'register.noPasswordNotice': 'No password required! Simply log in with your phone number.',
    'register.fullName': 'Full Name *',
    'register.fullNamePlaceholder': 'Enter your full name',
    'register.phone': 'Phone Number *',
    'register.phonePlaceholder': '+1234567890 or 0123456789',
    'register.phoneHint': 'Use this phone number to log in later',
    'register.email': 'Email (Optional)',
    'register.factoryName': 'Factory Name (Optional)',
    'register.address': 'Address (Optional)',
    'register.submit': 'Create Account',
    'register.submitting': 'Creating Account...',
    'register.alreadyHaveAccount': 'Already have an account?',
    'register.signInLink': 'Sign in',
    'register.backToLogin': '← Back to Login',
    'unauthorized.title': '403',
    'unauthorized.subtitle': 'Access Denied',
    'unauthorized.message': "You don't have permission to access this page.",
    'unauthorized.goHome': 'Go to Home',

    'orders.modalAddTitle': 'Add New Order',
    'orders.modalEditTitle': 'Edit Order',
    'orders.materialPrint': 'Material (Print)',
    'orders.productItem': 'Product (Item)',
    'orders.material': 'Material',
    'orders.product': 'Product',
    'orders.selectMaterial': 'Select material',
    'orders.selectProduct': 'Select product',
    'orders.quantity': 'Quantity',
    'orders.orderSizeAuto': 'Order Size (m)',
    'orders.orderSizeAutoHint': 'auto = repeats × height',
    'orders.designLink': 'Design Link',
    'orders.selectClient': 'Select client',
    'orders.clientName': 'Client Name',
    'orders.updateDesignTitle': 'Update Design Information',
    'orders.viewDesign': 'View Design',
    'orders.noDesign': 'No Design',
    'orders.noLink': 'No link',
    'orders.noNotes': 'No notes',
    'orders.noOrders': 'No orders found',
    'orders.viewOnly': 'View Only',
    'orders.saving': 'Saving...',
    'orders.updateOrder': 'Update Order',
    'orders.updateDesign': 'Update Design',
    'orders.type': 'Type',
    'orders.loading': 'Loading orders...',
    'orders.noMaterials': 'No materials available.',
    'orders.noProducts': 'No products available.',

    // Materials Extended Keys
    'materials.materialName': 'Material Name',
    'materials.category': 'Category',
    'materials.unit': 'Unit',
    'materials.supplier': 'Supplier',
    'materials.selectSupplier': 'Select a supplier',
    'materials.minStockLevel': 'Minimum Stock Level',
    'materials.currentStock': 'Current Stock',
    'materials.costPerUnit': 'Cost per Unit (EGP)',
    'materials.sellingPrice': 'Selling Price (EGP)',
    'materials.sellingPriceHint': 'This price will be used directly when material is selected in orders',
    'materials.description': 'Description',
    'materials.descriptionPlaceholder': 'Enter material description (optional)',
    'materials.saving': 'Saving...',
    'materials.updateMaterial': 'Update Material',
    'materials.addNewMaterial': 'Add Material',
    'materials.inStock': 'In Stock',
    'materials.lowStock': 'Low Stock',
    'materials.outOfStock': 'Out of Stock',
    'materials.minLevel': 'Min Level',
    'materials.orderType': 'Order Type',
    'materials.yes': 'Yes',
    'materials.no': 'No',
    'materials.noSupplier': 'No Supplier',
    'materials.categories.paper': 'Paper',
    'materials.categories.ink': 'Ink',
    'materials.categories.chemicals': 'Chemicals',
    'materials.categories.packaging': 'Packaging',
    'materials.categories.tools': 'Tools',
    'materials.categories.other': 'Other',
    'materials.units.kg': 'Kilogram (kg)',
    'materials.units.liter': 'Liter (L)',
    'materials.units.piece': 'Piece',
    'materials.units.box': 'Box',
    'materials.units.roll': 'Roll',
    'materials.units.sheet': 'Sheet',

    // Suppliers Extended Keys
    'suppliers.subtitle': 'Manage your suppliers and their contact information',
    'suppliers.name': 'Supplier Name',
    'suppliers.contactPerson': 'Contact Person',
    'suppliers.phone': 'Phone Number',
    'suppliers.email': 'Email',
    'suppliers.address': 'Address',
    'suppliers.businessInfo': 'Business Information',
    'suppliers.paymentTerms': 'Payment Terms',
    'suppliers.status': 'Status',
    'suppliers.materialsSupplied': 'Materials Supplied',
    'suppliers.materialsSuppliedDesc': 'Select the materials this supplier can provide',
    'suppliers.selectedMaterials': 'Selected Materials',
    'suppliers.additionalNotes': 'Additional Notes',
    'suppliers.saving': 'Saving...',
    'suppliers.updateSupplier': 'Update Supplier',
    'suppliers.addNewSupplier': 'Add Supplier',
    'suppliers.searchPlaceholder': 'Search by supplier, contact person, or phone...',
    'suppliers.totalSuppliers': 'Total Suppliers',
    'suppliers.activeSuppliers': 'Active Suppliers',
    'suppliers.active': 'Active',
    'suppliers.inactive': 'Inactive',
    'suppliers.noSuppliers': 'No suppliers found',
    'suppliers.noSuppliersDesc': 'Start by adding your first supplier to manage your inventory',
    'suppliers.noMaterials': 'No materials specified',
    'suppliers.deleteSelected': 'Delete Selected',
    'suppliers.selectedCount': 'selected',
    'suppliers.termsCash': 'Cash',
    'suppliers.termsNet15': 'Net 15 Days',
    'suppliers.termsNet30': 'Net 30 Days',
    'suppliers.termsNet60': 'Net 60 Days',

    // Purchases Extended Keys
    'purchases.subtitle': 'Manage purchase orders and track deliveries',
    'purchases.purchaseNumber': 'Purchase #',
    'purchases.orderInfo': 'Order Information',
    'purchases.orderDate': 'Order Date',
    'purchases.expectedDelivery': 'Expected Delivery',
    'purchases.items': 'Purchase Items',
    'purchases.addItem': '+ Add Item',
    'purchases.selectMaterial': 'Select Material',
    'purchases.quantity': 'Quantity',
    'purchases.unitCost': 'Unit Cost',
    'purchases.totalCost': 'Total Cost',
    'purchases.notesPlaceholder': 'Additional notes or special instructions...',
    'purchases.noItemsYet': 'No items added yet.',
    'purchases.selectSupplierFirst': 'Select a supplier first to add items.',
    'purchases.statusOrdered': 'Ordered',
    'purchases.statusReceived': 'Received',
    'purchases.markOrdered': 'Mark as Ordered',
    'purchases.markReceived': 'Mark as Received',
    'purchases.saving': 'Saving...',
    'purchases.updatePurchase': 'Update Purchase Order',
    'purchases.createPurchase': 'New Purchase Order',
    'purchases.tbd': 'TBD',
    'purchases.noPurchases': 'No purchase orders found',

    // Search and Filters Reusable Keys
    'search.searchBtn': 'Search',
    'search.allClients': 'All Clients',
    'search.allStatuses': 'All Statuses',
    'search.fromDate': 'From Date',
    'search.toDate': 'To Date',
    'search.clearFilters': 'Clear Filters',
    'search.defaultPlaceholder': 'Search...',

    // Pagination Reusable Keys
    'pagination.showing': 'Showing',
    'pagination.to': 'to',
    'pagination.of': 'of',
    'pagination.results': 'results',
    'pagination.first': 'First page',
    'pagination.prev': 'Previous page',
    'pagination.next': 'Next page',
    'pagination.last': 'Last page',

    // Users Extended Keys
    'users.role': 'Role',
    'users.password': 'Password',
    'users.passwordHint': '(leave empty to keep current)',
    'users.activate': 'Activate',
    'users.deactivate': 'Deactivate',
    'users.processing': 'Processing action, please wait...',
    'users.noUsers': 'No users found',

    // Financial Stats & Reports
    'finance.totalOrders': 'Total Orders',
    'finance.totalRevenue': 'Total Revenue',
    'finance.totalDeposits': 'Total Deposits',
    'finance.totalRemaining': 'Total Remaining',
    'finance.ordersByStatus': 'Orders by Status',
    'finance.count': 'Count',
    'finance.totalValue': 'Total Value',
    'finance.exportPDF': 'Export to PDF',

    // Common Currency and Units
    'common.egp': 'EGP',
    'common.meters': 'm',
    'common.loading': 'Loading...',
    'common.processing': 'Processing...',
    'status.all': 'All',
    'status.read': 'Read',

    // Purchases Additional
    'purchases.supplier': 'Supplier',
    'purchases.selectSupplier': 'Select Supplier',
    'purchases.totalAmount': 'Total Amount',
    'purchases.loading': 'Loading purchase orders...',
    'purchases.noPurchasesDesc': 'Start by creating your first purchase order.',
    'purchases.searchPlaceholder': 'Search by purchase number or supplier...',

    // Products Additional
    'products.composition': 'Composition',
    'products.noMaterials': 'No materials added',
    'products.productName': 'Product Name',
    'products.materialComposition': 'Material Composition',

    // Users Additional
    'users.allRoles': 'All Roles',
    'users.username': 'Username',
    'users.fullName': 'Full Name',
    'users.email': 'Email',
    'users.loading': 'Loading users...',
    'users.editUser': 'Edit User',

    // Notifications Additional
    'notifications.category': 'Category',
    'notifications.allCategories': 'All Categories',
    'notifications.payments': 'Payments',
    'notifications.system': 'System',

    // Client Reports
    'clientReports.subtitle': 'View detailed financial information for each client including orders, invoices, and payments',
    'clientReports.selectClient': 'Select Client:',
    'clientReports.selectPlaceholder': '-- Select a client --',
    'clientReports.noOrders': 'No orders found for this client.',
    'clientReports.endpointNotFound': 'Client report endpoint not found. Please restart the backend server.',
    'clientReports.fetchError': 'Failed to fetch client report',

    // Extended Finance
    'finance.totalPaid': 'Total Paid',
    'finance.outstanding': 'Outstanding',
    'finance.activeClients': 'Active Clients',
    'finance.totalInvoices': 'Total Invoices',
    'finance.totalOwed': 'Total Owed',
    'finance.orderAmount': 'Order Amount',
    'finance.invoiceAmount': 'Invoice Amount',
    'finance.paymentStats': 'Payment Statistics',
    'finance.avgOrderValue': 'Average Order Value',
    'finance.collectionRate': 'Payment Collection Rate',
    'finance.fullyPaidOrders': 'Fully Paid Orders',
    'finance.partiallyPaidOrders': 'Partially Paid Orders',
    'finance.unpaidOrders': 'Unpaid Orders',
    'finance.revenueByMonth': 'Revenue by Month',
    'finance.invoicesByStatus': 'Invoices by Status',

    // Date Range and Reports
    'report.allTime': 'All Time',
    'report.last7Days': 'Last 7 Days',
    'report.lastMonth': 'Last Month',
    'report.lastQuarter': 'Last Quarter',
    'report.lastYear': 'Last Year',
    'report.customRange': 'Custom Range',

    // Store Order Statuses
    'store.orderNew': 'New',
    'store.orderPrinted': 'Printed',
    'store.orderShipped': 'Shipped',
    'store.orderDelivered': 'Delivered',
    'store.orderCancelled': 'Cancelled',

    // Relative Time
    'time.justNow': 'Just now',
    'time.mAgo': 'm ago',
    'time.hAgo': 'h ago',
    'time.dAgo': 'd ago',

    // Shared & Page-specific Keys
    'orders.subtitle': 'Latest production & work orders received',
    'action.select': 'Select',
    'action.update': 'Update',
    'action.create': 'Create',
    'confirm.deleteClient': 'Are you sure you want to delete this client?',
    'categories': 'Categories',
    'categoriesSubtitle': 'Manage store product and design categories',
    'field.category': 'Category',
    'field.name': 'Name',
    'common.select': 'Select...',
    'markAsRead': 'Mark as read',
    'users.searchPlaceholder': 'Search by username, name, or email...',
    'register.alreadyRegistered': 'This phone number is already registered. Please try logging in instead.',

    // Website & Store Settings
    'websiteSettings.title': 'Website & Store Settings',
    'websiteSettings.subtitle': 'Manage your public website content, Coveraty store, and appearance',
    'websiteSettings.mainWebsite': 'Main Website',
    'websiteSettings.storeWebsite': 'Coveraty Store',
    'websiteSettings.hero': 'Hero Section',
    'websiteSettings.about': 'About Section',
    'websiteSettings.services': 'Services',
    'websiteSettings.whyUs': 'Why Choose Us',
    'websiteSettings.gallery': 'Gallery',
    'websiteSettings.contact': 'Contact Information',
    'websiteSettings.branding': 'Logo & Colors',
    'websiteSettings.seo': 'SEO Settings',
    'websiteSettings.saveChanges': 'Save Changes',
    'websiteSettings.savedSuccess': 'Settings updated successfully!',
    'websiteSettings.saveFailed': 'Failed to update settings',
    'websiteSettings.preview': 'Preview Website',
    'landing.getStarted': 'Get Started',
    'landing.learnMore': 'Learn More',
    'landing.ourServices': 'Our Services',
    'landing.whyChooseUs': 'Why Choose Us',
    'landing.getInTouch': 'Get In Touch',
    'landing.followUs': 'Follow Us',
    'landing.scanToConnect': 'Scan to connect',
    'landing.allRightsReserved': 'All rights reserved © Sendroli Group',

    // Missing keys & localization additions
    'validation.phoneInvalid': 'Please enter a valid phone number',
    'validation.emailInvalid': 'Please enter a valid email address',
    'msg.checkFields': 'Please check required fields',
    'common.exportError': 'Failed to export file',
    'common.print': 'Print',
    'common.actions': 'Actions',
    'common.view': 'View',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.page': 'Page',
    'common.of': 'of',
    'common.pageOf': 'Page {current} of {total}',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'finance.paid': 'Paid',
    'client.loadError': 'Failed to load client details',
    'client.notFound': 'Client not found',
    'client.name': 'Client Name',
    'client.phone': 'Phone Number',
    'client.factoryName': 'Factory Name',
    'client.address': 'Address',
    'client.deleteError': 'Failed to delete client',
    'client.loadingClients': 'Loading clients...',
    'client.addNew': 'Add New Client',
    'client.searchPlaceholder': 'Search by client name, phone, or factory...',
    'client.noClientsFound': 'No clients found',
    'table.scrollPrompt': 'Scroll horizontally for more columns',
    'orders.size': 'Size',
    'orders.status': 'Status',
    'orders.loadError': 'Failed to load order',
    'orders.notFound': 'Order not found',
    'orders.notes': 'Notes',
    'orders.client': 'Client',
    'field.createdAt': 'Created At',
    'field.email': 'Email',
    'field.address': 'Address',
    'field.value': 'Value',
    'field.orderAmount': 'Order Amount',
    'field.invoiceAmount': 'Invoice Amount',
    'field.month': 'Month',
    'sidebar.clients': 'Clients',
    'nav.logout': 'Sign Out',
    'nav.clientPortal': 'Client Portal',
    'notification.orderStatusChanged': 'Order Status Changed',
    'notification.orderUpdated': 'Order Updated',
    'notification.orderDeleted': 'Order Deleted',
    'notification.clientUpdated': 'Client Updated',
    'notification.clientDeleted': 'Client Deleted',
    'notification.invoiceUpdated': 'Invoice Updated',
    'notification.materialCreated': 'New Material Added',
    'notification.materialUpdated': 'Material Updated',
    'notification.paymentReceived': 'Payment Received',
    'notification.lowStockAlert': 'Low Stock Alert',
    'notification.systemAlert': 'System Alert',
    'notification.allCaughtUp': "You're all caught up! No unread notifications.",
    'notification.noNotificationsYet': "You don't have any notifications yet.",
    'notification.markAllAsRead': 'Mark all as read',
    'dashboard.kpi': 'KPI',
    'dashboard.orders': 'Work Orders',
    'financialReport.revenueByMonth': 'Revenue by Month',
    'clients.clientName': 'Client Name',
    'coveraty.shipping': 'Shipping',
    'auth.singleDeviceLogin': 'Single Device Login',
    'auth.loggingInFrom': 'Logging in from:',
    'auth.phoneHint': 'Phone-only login — no password is required for client accounts.',
    'auth.phoneNotFoundWarning': "No account found for this phone number. If you're a staff/admin, try logging in with username or email and your password.",
    'auth.optionalForPhone': 'Optional for phone login',
    'auth.securityNotice': 'Security Notice: Only one device can be logged in at a time. Use "Force Login Here" to terminate other sessions.',
    'auth.dontHaveAccount': "Don't have an account?",
    'auth.register': 'Register',
    'auth.login': 'Login',
    'auth.loggingIn': 'Logging in...',
    'suppliers.confirmDeleteOne': 'Are you sure you want to delete this supplier? This action cannot be undone.',
    'suppliers.confirmDeleteMultiple': 'Are you sure you want to delete {count} selected suppliers?',
    'suppliers.deleteFailed': 'Failed to delete supplier. Please try again.',
    'suppliers.loading': 'Loading suppliers...',
    'materials.confirmDelete': 'Are you sure you want to delete this material? This action cannot be undone.',
    'materials.deleteFailed': 'Failed to delete material. Please try again.',
    'materials.allStockLevels': 'All Stock Levels',
    'materials.totalMaterials': 'Total Materials',
    'materials.lowStockItems': 'Low Stock Items',
    'unit.kg': 'kg',
    'unit.sheet': 'sheet',
    'unit.meter': 'meter',
    'unit.roll': 'roll',
    'unit.box': 'box',
    'unit.piece': 'piece',
    'unit.packet': 'packet',
    'category.paper': 'Paper & Cardboard',
    'category.ink': 'Inks & Printing',
    'category.plates': 'Plates & Zinc',
    'category.chemicals': 'Chemicals & Solutions',
    'category.packaging': 'Packaging',
    'category.raw_material': 'Raw Materials',
    'category.general': 'General',
    'coveraty.brandUpdated': 'Brand updated successfully',
    'coveraty.brandCreated': 'Brand created successfully',
    'coveraty.brandSaveError': 'Error saving brand',
    'coveraty.brandDeleteConfirm': 'Are you sure you want to delete this brand?',
    'coveraty.brandDeleted': 'Brand deleted',
    'coveraty.brandDeleteError': 'Error deleting brand',
    'coveraty.modelUpdated': 'Model updated successfully',
    'coveraty.modelCreated': 'Model created successfully',
    'coveraty.modelSaveError': 'Error saving model',
    'coveraty.modelDeleteConfirm': 'Are you sure you want to delete this model?',
    'coveraty.modelDeleted': 'Model deleted',
    'coveraty.modelDeleteError': 'Error deleting model',
    'coveraty.categoryFetchError': 'Failed to fetch categories',
    'coveraty.categoryUpdated': 'Category updated successfully',
    'coveraty.categoryCreated': 'Category created successfully',
    'coveraty.categorySaveError': 'Error saving category',
    'coveraty.categoryDeleteConfirm': 'Are you sure you want to delete this category?',
    'coveraty.categoryDeleted': 'Category deleted successfully',
    'coveraty.categoryDeleteError': 'Error deleting category',
    'coveraty.designDeleteConfirm': 'Are you sure you want to delete this design?',
    'coveraty.designDeleted': 'Design deleted successfully',
    'coveraty.dataLoadError': 'Failed to load data',
    'coveraty.designFillRequired': 'Please fill all fields and select at least one model',
    'coveraty.designUpdated': 'Design updated successfully',
    'coveraty.designAdded': 'Design added successfully',
    'coveraty.shippingUpdated': 'Shipping zone updated successfully',
    'coveraty.shippingCreated': 'Shipping zone created successfully',
    'coveraty.shippingSaveError': 'Error saving shipping zone',
    'coveraty.shippingDeleteConfirm': 'Are you sure you want to delete this shipping zone?',
    'coveraty.shippingDeleted': 'Shipping zone deleted successfully',
    'coveraty.shippingDeleteError': 'Error deleting shipping zone',
    'coveraty.ordersFetchError': 'Failed to load orders',
    'coveraty.orderStatusUpdated': 'Order status updated',
    'coveraty.orderStatusUpdateError': 'Failed to update status',
    'coveraty.noImage': 'No Image',
    'coveraty.customDesign': 'Custom Design',
    'coveraty.unknownName': 'Unknown Name',
    'products.loadError': 'Failed to load products',
    'products.fillRequired': 'Please fill in required fields',
    'products.updated': 'Product updated successfully',
    'products.created': 'Product created successfully',
    'products.saveError': 'Failed to save product',
    'products.deleteConfirm': 'Are you sure you want to delete this product?',
    'products.deleted': 'Product deleted successfully',
    'products.deleteError': 'Failed to delete product',
    'orders.deleteConfirm': 'Are you sure you want to delete this order?',
    'purchases.deleteFailed': 'Failed to delete purchase order. Please try again.',
    'websiteSettings.accessDenied': 'Access Denied',
    'websiteSettings.accessDeniedDesc': "You don't have permission to access this page. Only administrators can modify website settings.",
    'websiteSettings.chooseImage': 'Choose Image',
    'websiteSettings.chooseLogo': 'Choose Logo',
    'websiteSettings.chooseQr': 'Choose QR Code',
    'websiteSettings.remove': 'Remove',
    'websiteSettings.uploading': 'Uploading...',
    'websiteSettings.arField': 'In Arabic',
    'websiteSettings.enField': 'In English',
    'websiteSettings.titleField': 'Title',
    'websiteSettings.tagline': 'Tagline',
    'websiteSettings.description': 'Description',
    'websiteSettings.mission': 'Mission',
    'websiteSettings.vision': 'Vision',
    'websiteSettings.ctaText': 'CTA Button Text',
    'websiteSettings.ctaLink': 'CTA Button Link',
    'websiteSettings.bgImage': 'Background Image',
    'websiteSettings.chooseBgImage': 'Choose Background Image',
    'websiteSettings.sectionTitle': 'Section Title',
    'websiteSettings.icon': 'Icon',
    'websiteSettings.image': 'Image',
    'websiteSettings.category': 'Category',
    'websiteSettings.phone': 'Phone',
    'websiteSettings.whatsapp': 'WhatsApp',
    'websiteSettings.email': 'Email',
    'websiteSettings.locationName': 'Map Location Name',
    'websiteSettings.mapsEmbed': 'Google Maps Embed Code',
    'websiteSettings.facebook': 'Facebook URL',
    'websiteSettings.instagram': 'Instagram URL',
    'websiteSettings.linkedin': 'LinkedIn URL',
    'websiteSettings.qrCode': 'QR Code Image',
    'websiteSettings.logo': 'Website Logo',
    'websiteSettings.primaryColor': 'Primary Color',
    'websiteSettings.secondaryColor': 'Secondary Color',
    'websiteSettings.accentColor': 'Accent Color',
    'websiteSettings.gradientStart': 'Gradient Start',
    'websiteSettings.gradientEnd': 'Gradient End',
    'websiteSettings.metaTitle': 'Meta Title',
    'websiteSettings.metaDescription': 'Meta Description',
    'websiteSettings.storeName': 'Store Name',
    'websiteSettings.addService': 'Add Service',
    'websiteSettings.addFeature': 'Add Feature',
    'websiteSettings.addGalleryItem': 'Add Gallery Item',
    'websiteSettings.whyChooseStore': 'Why Choose Store',
    'websiteSettings.storeServices': 'Store Services',
    'websiteSettings.storeGallery': 'Store Gallery',
    'websiteSettings.storeAbout': 'Store About Section',
    'websiteSettings.storeHero': 'Store Hero Section',
    'websiteSettings.serviceItem': 'Service #{num}',
    'websiteSettings.featureItem': 'Feature #{num}',
    'websiteSettings.galleryItem': 'Gallery Item #{num}',
    'websiteSettings.iconEmoji': 'Icon (Emoji)',
    'websiteSettings.active': 'Active',
    'websiteSettings.keywords': 'Keywords (comma-separated)',
    'websiteSettings.storeLogo': 'Store Logo',
    'websiteSettings.loadFailed': 'Failed to load website settings',
    'websiteSettings.loading': 'Loading website settings...',
    'websiteSettings.mapLocation': 'Map Location Name',
    'websiteSettings.mapEmbed': 'Google Maps Embed Code',
    'common.image': 'Image',
    'orders.deleteFailed': 'Failed to delete order',
    'filter.allStatuses': 'All Statuses',
    'orders.updateStatus': 'Update Status',
    'orders.noDesignYet': 'No design yet',
    'header.shortcutsHelp': 'Keyboard Shortcuts Help',
    'coveraty.designDeleteError': 'Failed to delete design',
    'notification.supplierCreated': 'Supplier Added',
    'notification.supplierUpdated': 'Supplier Updated',
    'notification.markReadError': 'Failed to mark notification as read',
    'notification.allMarkedRead': 'All notifications marked as read',
    'notification.markAllReadError': 'Failed to mark all notifications as read',
    'notification.confirmDeleteOne': 'Are you sure you want to delete this notification?',
    'notification.deleted': 'Notification deleted',
    'notification.deleteError': 'Failed to delete notification',
    'notification.confirmDeleteAllRead': 'Are you sure you want to delete all read notifications?',
    'notification.allReadDeleted': 'All read notifications deleted',
    'notification.deleteAllError': 'Failed to delete notifications',
    'invoices.officialInvoice': 'Official Tax Invoice',
    'orders.jobTicket': 'Production Job Ticket'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('sendroli_lang');
      return saved === 'ar' ? 'ar' : 'en'; // English is primary default
    } catch {
      return 'en';
    }
  });

  const isRTL = language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    try {
      localStorage.setItem('sendroli_lang', language);
    } catch (e) {
      console.warn('LocalStorage error:', e);
    }

    document.documentElement.dir = dir;
    document.documentElement.lang = language;
    if (isRTL) {
      document.body.classList.add('rtl-layout');
      document.body.classList.remove('ltr-layout');
    } else {
      document.body.classList.add('ltr-layout');
      document.body.classList.remove('rtl-layout');
    }
  }, [language, dir, isRTL]);

  const toggleLanguage = () => {
    setLanguageState(prev => (prev === 'ar' ? 'en' : 'ar'));
  };

  const setLanguage = (lang) => {
    if (lang === 'ar' || lang === 'en') {
      setLanguageState(lang);
    }
  };

  const t = useMemo(() => {
    return (key, fallback = '') => {
      const currentDict = translations[language] || translations.en;
      let text = '';
      if (currentDict && currentDict[key] !== undefined) {
        text = currentDict[key];
      } else if (translations.en && translations.en[key] !== undefined) {
        text = translations.en[key];
      } else {
        text = fallback || key;
      }
      if (language === 'ar' && typeof text === 'string') {
        return text.replace(/سندروالي/g, 'سيندرولي');
      }
      return text;
    };
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      isRTL,
      dir,
      setLanguage,
      toggleLanguage,
      t
    }),
    [language, isRTL, dir, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
